﻿/**
 * Translation dictionaries. The English object defines the dictionary *shape*;
 * `Widen` strips its literal-string types so other locales can use any string
 * with the same key structure.
 *
 * Templating: `{name}` placeholders are replaced at call time via `t(key, vars)`.
 */

type Widen<T> = T extends string ? string : T extends object ? { [K in keyof T]: Widen<T[K]> } : T;

const enSource = {
  app: {
    name: "ONPOINT",
    tagline: "Hoofdklasse · Fantasy Baseball",
  },
  nav: {
    home: "Home",
    league: "League",
    settings: "Settings",
    more: "More",
    moreEmpty: "Open a league to see its sections here.",
    primary: "Primary",
    leagueMenu: "League menu",
    commandDeck: "Command deck",
    joinLeagueToUnlock: "Join a league to unlock league navigation.",
    menuItems: "{count} menu items",
    signOut: "Sign out",
    language: "Language",
  },
  locale: {
    en: "English",
    es: "Español",
    nl: "Nederlands",
  },
  auth: {
    signInTitle: "Welcome back",
    signInSubtitle: "Sign in to manage your league.",
    signUpTitle: "Create your account",
    signUpSubtitle: "It takes about a minute.",
    email: "Email",
    password: "Password",
    confirmPassword: "Confirm password",
    showPassword: "Show password",
    hidePassword: "Hide password",
    name: "Full name",
    signIn: "Sign in",
    signUp: "Create account",
    newHere: "New here?",
    alreadyHaveOne: "Already have an account?",
    createOne: "Create one",
    signInLink: "Sign in",
    errorGeneric: "Something went wrong. Try again.",
  },
  home: {
    welcome: "Welcome, {name}",
    homeBase: "Your fantasy baseball home base.",
    getStarted: "Get started",
    noLeagueYet: "No league yet",
    noLeagueYetBody: "Create a league or join one with an invite code.",
    onboardTitle: "One league, one season.",
    onboardLead:
      "Spin up a private league for your friends, or jump into one with an invite code. The Hoofdklasse, your way.",
    createLeague: "Create a league",
    createLeagueDesc: "You'll be the commissioner. Pick the rules, invite the rest.",
    joinLeague: "Join with a code",
    joinLeagueDesc: "Got an 8-character invite? Drop it in and pick your team name.",
    or: "or",
  },
  league: {
    overview: "League overview",
    season: "Season {year}",
    managers: "Managers",
    managersOf: "{count} of {max}",
    rosterSize: "{count}-player rosters",
    inviteCode: "Invite code",
    inviteCodeHelp: "Share this with your friends. Anyone with the code can join.",
    copy: "Copy",
    copied: "Copied",
    regenerate: "Regenerate",
    state: {
      setup: "Setup",
      drafting: "Drafting",
      active: "Active",
      complete: "Complete",
    },
    commissioner: "Commissioner",
    startDraft: "Start the draft",
    needTwo: "At least two managers required",
    yourTeam: "Your team",
    activity: {
      eyebrow: "Live league feed",
      title: "Roster activity",
      open: "Open roster activity",
      close: "Close roster activity",
      loading: "Loading roster activity...",
      empty: "No roster changes yet.",
      clearedEmpty: "You're caught up. Show history to review older changes.",
      clear: "Clear",
      clearing: "Clearing...",
      showHistory: "Show history",
      showLatest: "Show latest",
      rowTitle: "{team} picked {player}",
      rowMeta: "Dropped {dropped}",
      tradeRowTitle: "{team} traded for {player}",
      tradeRowMeta: "Sent {sent}",
    },
    trades: {
      title: "Trades",
      subtitle: "Offer a one-for-one swap, then let the other manager accept or decline.",
      newTrade: "New trade",
      incoming: "Incoming",
      outgoing: "Sent",
      history: "History",
      emptyIncoming: "No incoming offers.",
      emptyOutgoing: "No sent offers.",
      emptyHistory: "No resolved trades yet.",
      offerLine: "{from} offers {offered} for {requested}",
      expires: "Expires {date}",
      resolved: "Resolved {date}",
      accept: "Accept",
      decline: "Decline",
      cancel: "Cancel",
      accepting: "Accepting...",
      declining: "Declining...",
      cancelling: "Cancelling...",
      proposeTitle: "Propose trade",
      proposeSubtitle: "Choose who you want and what you are offering back.",
      chooseManager: "Choose manager",
      requestedPlayer: "Requested player",
      offeredPlayer: "Your offer",
      chooseRequested: "Choose a player to request",
      chooseOffered: "Choose one of yours",
      summary: "Review",
      youReceive: "You receive",
      youSend: "You send",
      back: "Back",
      confirm: "Confirm trade",
      submit: "Send offer",
      sending: "Sending...",
      unavailable: "Trades are available after the draft while the league is active.",
      openLimit: "{count} open offers allowed",
      proposeFromRoster: "Offer trade",
      statuses: {
        pending: "Pending",
        accepted: "Accepted",
        declined: "Declined",
        cancelled: "Cancelled",
        expired: "Expired",
        superseded: "Closed",
      },
      created: "Trade request sent.",
      accepted: "Trade accepted.",
      declined: "Trade declined.",
      cancelled: "Trade cancelled.",
      failed: "Trade action failed.",
    },
    activeRoster: {
      eyebrow: "Active roster",
      title: "Active roster size",
      description:
        "How many of each manager's drafted players count toward standings. Roster size: {roster}.",
      fieldLabel: "Active count",
      allCount: "All drafted players count toward season totals.",
      partialCount: "Top {active} score · {bench} on the bench.",
      save: "Save",
      saving: "Saving…",
      invalid: "Active count must be between 1 and {roster}.",
    },
    manage: {
      title: "League settings",
      description:
        "Edit league details or remove this league entirely. Locked once the draft begins.",
      detailsTitle: "Details",
      maxManagersHint: "Already joined: {count}.",
      edit: "Edit",
      save: "Save changes",
      cancel: "Cancel",
      remove: "Remove",
      removeMember: "Remove {team} from the league",
      scoringStart: "Scoring start",
      scoringStartAll: "All season",
      scoringStartTitle: "Scoring window",
      scoringStartDescription:
        "Games before this date do not count for this league, but remain available for other leagues.",
      clearScoringStart: "Clear",
      draftTitle: "Draft control",
      reset: "Reset draft",
      resetConfirm: "Yes, reset",
      resetDescription:
        "Cancels the current draft and returns the league to setup. All picks are discarded.",
      dangerZone: "Danger zone",
      deleteDescription: "Permanently deletes the league, members, and any draft picks.",
      delete: "Delete league",
      deleteConfirm: "Yes, delete it",
      pitcherDraft: {
        title: "Pitcher draft",
        description:
          "Add pitcher slots to this league and run a snake draft just for pitchers. Round 1 is reversed from the original draft (worst hitter team picks first).",
        slotsLabel: "Pitcher slots",
        slotsHint: "1–10. Total roster grows by this much.",
        setSlots: "Set pitcher slots",
        start: "Start pitcher draft",
        setFirst: "Set pitcher slots first.",
        slotsSaved: "Pitcher slots set to {count}.",
        invalidSlots: "Pitcher slots must be a whole number between 1 and 10.",
      },
      endLeague: {
        title: "End league",
        description:
          "Finalize standings and freeze the league. Pickups and stat-driven total changes stop for this league. This cannot be undone.",
        confirm: "End league",
        confirmHint: "Type the league name to confirm.",
        success: "League ended.",
      },
      ended: "League ended on {date}",
      seasonEnd: {
        title: "Season end",
        description:
          "Once this date passes and every scheduled game on or before it is final, the league auto-finalizes.",
        fieldLabel: "Season end date",
        clear: "Clear",
      },
    },
    games: {
      title: "Games",
      subtitle: "Games",
      empty: "No games this season yet.",
      emptyTitle: "Quiet schedule",
      comingSection: "Coming games",
      previousSection: "Previous games",
      columnDate: "Date",
      columnHome: "Home",
      columnAway: "Away",
      detail: {
        eyebrow: "Box score",
        backToList: "All games",
        yourPlayer: "Your player",
        draftedBy: "Drafted by {team}",
        youScored: "You scored {points} points in this game",
        nonePlayed: "None of your players appeared in this game.",
        noStatsYet: "No stats entered yet for this game.",
        showOnlyMine: "Only my players",
        showAll: "All players",
      },
      headers: {
        AB: "AB",
        H: "H",
        D2B: "2B",
        D3B: "3B",
        HR: "HR",
        RBI: "RBI",
        R: "R",
        BB: "BB",
        SB: "SB",
        CS: "CS",
        SO: "SO",
        PTS: "PTS",
        IP: "IP",
        ER: "ER",
        DEC: "DEC",
      },
    },
    standings: {
      title: "Standings",
      subtitle: "Where everyone sits, refreshed live as box scores land.",
      empty: "No points scored yet — wait for the first box score.",
      emptyTitle: "Quiet leaderboard",
      regular: "Regular season",
      champion: "Champion",
      columnRank: "#",
      columnTeam: "Team",
      columnPoints: "PTS",
      columnGames: "G",
      youLabel: "You",
      weeklyChart: {
        title: "Points per week",
        subtitle: "Season points by week.",
        empty: "Not enough weekly data to chart yet.",
      },
      topPlayers: {
        title: "Top performers",
        subtitle: "Highest-scoring players across the league.",
        seeAll: "See all players",
        empty: "No player stats yet.",
      },
    },
    scoring: {
      title: "Scoring",
      subtitle: "How fantasy points are awarded. Read-only — set by the commissioner.",
      hitterTitle: "Batting",
      pitcherTitle: "Pitching",
      columns: {
        rule: "Event",
        abbr: "Abbr",
        points: "Points",
      },
    },
    players: {
      title: "Players",
      subtitle: "Every drafted player ranked by season fantasy points.",
      empty: "No drafted players have stats yet.",
      emptyTitle: "Empty leaderboard",
      columnRank: "#",
      columnPlayer: "Player",
      columnTeam: "KNBSB Team",
      columnDrafter: "Drafted by",
      columnGames: "G",
      columnPoints: "PTS",
      freeAgents: {
        title: "Free agents",
        subtitle:
          "Undrafted players sorted by season points. Pick one up to swap them onto your roster.",
        empty: "No undrafted players in the eligible pool.",
        searchPlaceholder: "Search player or team",
        noMatch: "No players match your search.",
        pickUp: "Pick up",
        picking: "Picking…",
        columnMove: "Pick",
        pickUpPlayer: "Pick up player",
        pickUpName: "Pick up {name}",
        replaceHint:
          "Choose the roster slot to replace. The incoming player inherits that pick slot and season totals recompute immediately.",
        noCompatibleSlots: "No compatible roster slots are available.",
        dismissError: "Dismiss error",
      },
      allPlayers: {
        title: "All drafted players",
        subtitle: "Every player currently rostered in this league, sorted by season points.",
        empty: "No players have been drafted yet.",
        columnTeam: "Team",
      },
      profile: {
        title: "Player profile",
        notFound: "Player not found",
        noLongerAvailable: "This player is no longer available in the league data.",
        backToPlayers: "Players",
        unknownPlayer: "Unknown player",
        team: "Team",
        fantasy: "Fantasy team",
        freeAgent: "Free agent",
        undrafted: "Undrafted",
        unassigned: "Unassigned",
        noCompetition: "No competition",
        pickMeta: "Pick {pick}",
        gameLogs: "Game logs",
        scoringRun: "Scoring run",
        noGameLogs: "No game logs recorded for this player yet.",
        hitting: "Hitting",
        pitching: "Pitching",
        viewGameAria: "Open game details for {opponent} on {date}",
      },
      dropPickup: {
        title: "Replace one of yours with {name}",
        titleFallback: "Replace one of yours",
        subtitle:
          "Choose which of your players to drop. The new player's season points will count for you immediately.",
        subtitlePitcher:
          "Choose which of your pitchers to drop. The new player's season points will count for you immediately.",
        subtitleHitter:
          "Choose which of your hitters to drop. The new player's season points will count for you immediately.",
        pickN: "Pick {n}",
        noRoster: "You don't have any drafted players to replace.",
        noPitchers: "You don't have any pitchers to replace.",
        noHitters: "You don't have any hitters to replace.",
        cancel: "Cancel",
      },
    },
    profile: {
      backToStandings: "All standings",
      eyebrow: "Manager",
      stats: {
        regularPoints: "Total",
        regularGames: "Regular G",
      },
      roster: {
        title: "Roster",
        subtitle: "Sorted by season fantasy points.",
        empty: "No drafted players.",
        columnPick: "Pick",
        columnPlayer: "Player",
        columnTeam: "Team",
        columnGames: "G",
        columnRegular: "Reg",
        columnTotal: "Total",
        autoBadge: "Auto",
      },
      recent: {
        title: "Contribution history",
        subtitle: "Latest roster scoring lines. Open the full view for oldest to newest.",
        empty: "No game contributions yet.",
        columnDate: "Date",
        columnPlayer: "Player",
        columnGame: "Game",
        columnPoints: "PTS",
        backToManager: "Manager profile",
        historyTitle: "Contributions",
      },
    },
    dashboard: {
      lastGame: "Last game",
      noLastGame: "No games played yet.",
      top3: "Top 3",
      rank: "Rank",
      points: "Points",
      pointsAbbr: "pts",
      finalStandingsTitle: "Final standings",
      finalStandingsEndedOn: "Season ended {date}",
      seeStandings: "See full standings",
      yourRoster: "Your roster",
      yourRosterSubtitle: "Form across each player's last three games.",
      rosterEmpty: "Draft hasn't completed yet.",
      columnLast3: "Last 3",
      columnForm: "Form",
      columnL3: "L3",
      columnLastThreePoints: "Last 3",
      columnSeason: "Season",
      columnTotal: "Total",
      lastThreeGames: "Last three games",
      formNoGame: "—",
      yourEdge: "Your edge",
      atTheTop: "Top of the table",
      gapToLeader: "{points} off the lead",
      aboveCutline: "{points} above cutline",
      belowCutline: "{points} below cutline",
      cutlineFirm: "Holding the cut by {points}",
      hottest: "Hottest · {name}",
      coldest: "Coldest · {name}",
      noForm: "No recent form yet.",
      benchDivider: "Bench · {count} players don't count",
      contributions: {
        title: "Recent contributions",
        subtitle: "Your roster's latest game lines — newest first.",
        sentence: "{player} contributed to {team} with {points}",
        statLine: "{kind} line",
        empty: "No game contributions yet.",
        seeAll: "See all",
        pageTitle: "Recent contributions",
        pageSubtitle: "Every eligible game your roster has scored in, newest first.",
        columnDate: "Date",
        columnPlayer: "Player",
        columnTeam: "Team",
        columnOpponent: "Opp",
        columnPoints: "PTS",
      },
      gameLog: {
        openAria: "Open game log for {name}",
        subtitle: "Every eligible game this player has scored in. Newest first.",
        empty: "No games scored yet.",
        hittingSection: "Hitting",
        pitchingSection: "Pitching",
        totals: "Totals · {count} G",
        columnDate: "Date",
        columnOpponent: "Opp",
        colAB: "AB",
        colR: "R",
        colH: "H",
        col2B: "2B",
        col3B: "3B",
        colHR: "HR",
        colRBI: "RBI",
        colBB: "BB",
        colSB: "SB",
        colK: "K",
        colFP: "FP",
        colIP: "IP",
        colER: "ER",
        colHA: "H",
        colBBA: "BB",
        colKA: "K",
        colDec: "Dec",
      },
    },
  },
  create: {
    title: "Create your league",
    subtitle: "Set the rules. Once the draft begins, settings lock for the season.",
    leagueName: "League name",
    teamName: "Your team name",
    season: "Season",
    maxManagers: "Max managers",
    rosterSize: "Roster size",
    hitterSlots: "Hitters per team",
    pitcherSlots: "Pitchers per team",
    totalRoster: "Total roster size: {total}",
    pickTimer: "Pick timer (seconds)",
    draftTimerEnabled: "Enable pick timer",
    draftTimerEnabledHelp: "Commissioner-paced. Managers pick at their own speed.",
    pickClock: "Pick clock",
    pickClockTimed: "Timed",
    pickClockUntimed: "Untimed",
    scoringStart: "Scoring start",
    scoringStartHelp:
      "Optional. Games before this date don't count for this league. Leave blank to count every eligible game.",
    capacityOk: "Capacity OK — {picks} picks of {pool} eligible players ({buffer}% buffer)",
    capacityFail: "Capacity check failed.",
    submit: "Create league",
    submitFail: "Failed to create league.",
  },
  join: {
    title: "Join a league",
    subtitle: "Got an invite code from the commissioner? Drop it in.",
    inviteCode: "Invite code",
    teamName: "Your team name",
    submit: "Join league",
    submitFail: "Failed to join league.",
  },
  settings: {
    title: "Settings",
    subtitle: "Account, language, and per-league configuration.",
    signedInAs: "Signed in as",
    signOut: "Sign out",
    language: "Language",
    languageHelp: "Choose how the app speaks to you.",
    tabs: {
      account: "Profiel",
      leagues: "Leagues",
      language: "Language",
    },
    leagues: {
      empty: {
        title: "No leagues yet",
        body: "Create or join a league from the home page to manage it here.",
      },
    },
  },
  validation: {
    required: "Required",
  },
  errors: {
    routeEyebrow: "Route error",
    routeTitle: "Could not load this screen",
    generic: "Something went wrong.",
    tryAgain: "Try again",
  },
  ui: {
    dataTable: {
      noRows: "No rows.",
      pageSize: "Page size",
      page: "Page",
      of: "of",
      prev: "Prev",
      next: "Next",
      search: "Search",
      clear: "Clear",
      rangeFrom: "{label} from",
      rangeTo: "{label} to",
    },
    datePicker: {
      selectDate: "Select a date",
      selectDateTime: "Select date & time",
      pickRange: "Pick a range",
      previousMonth: "Previous month",
      nextMonth: "Next month",
      time: "Time",
      hour: "Hour",
      minute: "Minute",
      clear: "Clear",
    },
    nightSearch: {
      placeholder: "Search players...",
      filterPlayers: "Filter players",
    },
  },
  pwa: {
    install: {
      title: "Install Fantasy",
      body: "Add it to your home screen for one-tap access and a native-app feel.",
      iosBody: "Tap the Share button in Safari, then choose 'Add to Home Screen'.",
      cta: "Install",
      dismiss: "Dismiss",
    },
    offline: {
      banner: "You're offline - last data shown",
    },
    update: {
      title: "Update available",
      body: "A new version of Fantasy is ready. Reload to apply.",
      cta: "Reload",
      dismiss: "Later",
    },
  },
  share: {
    button: "Share",
    failed: "Could not generate the share card.",
  },
  draft: {
    title: "Draft",
    onClock: {
      eyebrow: "On the clock",
      yourTurn: "Your pick",
      waitingFor: "Waiting on {team}",
      pickNumber: "Pick {pick} · Round {round}",
      pitcherPill: "PITCHERS ONLY",
    },
    timer: {
      secondsLeft: "{seconds}s",
      paused: "Paused",
      pausedOff: "Paused · timer off",
      expired: "Auto-picking…",
      off: "Timer off",
    },
    autoPick: {
      toggleLabel: "Auto-pick my favorites (15s)",
      eyebrow: "Auto-pick · 15s",
      body: "From my favorites, in order",
      tapToEnable: "Tap to enable",
      needFavorites: "Star at least one player to enable.",
      firingFromFavorites: "Auto-picking from favorites…",
      explainer: {
        title: "How auto-pick works",
        body1:
          "When the league timer is off and it's your turn, you have 15 seconds before auto-pick fires.",
        body2:
          "Only players from your favorites are picked, in order. If all your favorites are drafted, no pick is made and you stay on the clock.",
        body3: "You can turn it off any time.",
        confirm: "Got it — turn on",
        cancel: "Cancel",
      },
    },
    available: {
      title: "Available players",
      searchPlaceholder: "Search players",
      teamFilterAll: "All teams",
      favoritesChip: "Favorites ({count})",
      empty: "No players match.",
      emptyFavorites: "No favorites match — star a player to add them.",
      pickButton: "Pick",
      notYourTurn: "Not your turn",
    },
    myPicks: {
      title: "My picks",
      countLabel: "{count} picks",
      openButton: "My picks ({count})",
      openLabel: "Show my picks",
      empty: "You haven't drafted any players yet.",
      notAMember: "You're not a member of this league.",
    },
    nextPicks: {
      label: "Up next",
    },
    turnAlert: {
      title: "It's your turn",
      body: "You're on the clock — head to the Draft tab to make your pick.",
      goToDraft: "Go to Draft",
      dismiss: "Dismiss",
    },
    history: {
      title: "Pick history",
      empty: "No picks yet.",
      roundLabel: "Round {round}",
      autoBadge: "Auto",
      pickByManager: "{team} → {player}",
    },
    board: {
      title: "Draft board",
      titlePitcher: "Pitcher draft board",
      onClockPill: "On clock",
      notStarted: "Draft hasn't started yet.",
    },
    tabs: {
      draft: "Draft",
      board: "Draft board",
    },
    queue: {
      title: "My favorites",
      empty:
        "Star players to favorite them. The top of your favorites auto-picks if your timer expires.",
      addToQueue: "Add to favorites",
      removeFromQueue: "Remove from favorites",
      clearAll: "Clear",
      topUp: "Up next",
      pickNow: "Pick now",
      muteAlerts: "Mute alerts",
      unmuteAlerts: "Enable alerts",
      searchPlaceholder: "Filter favorites",
      noMatch: "No favorites match.",
      moveUp: "Move up",
      moveDown: "Move down",
    },
    roster: {
      title: "My roster",
      subtitle: "Players drafted to {team} this season.",
      empty: "You didn't draft any players.",
    },
    controls: {
      start: "Start draft",
      startPitcher: "Start pitcher draft",
      startPitcherConfirm:
        "Start the supplemental pitcher draft? Round 1 order is reversed from the main draft.",
      pause: "Pause",
      resume: "Resume",
      startConfirm: "Start the draft now?",
      timerOn: "Timer on",
      timerOff: "Timer off",
      cancel: "Cancel",
      disableTimerTitle: "Lift the pick clock?",
      disableTimerBody:
        "The league timer will stop. Members who turned on personal auto-pick will still get a 15s favorites-only clock; everyone else stays on the clock until they pick manually or you contact them.",
      disableTimerConfirm: "Yes, lift it",
      enableTimerTitle: "Restore the pick clock?",
      enableTimerBody:
        "The league timer will start counting down again from the full pick duration. Personal auto-pick clocks stop — auto-picks will use the league's standard random fallback when a member's favorites are exhausted.",
      enableTimerConfirm: "Yes, restore it",
    },
    errors: {
      notYourTurn: "It's not your pick.",
      alreadyDrafted: "That player has already been drafted.",
      draftNotActive: "The draft isn't active.",
      slotFull: "Your roster slots for that player are full.",
      notAMember: "You're no longer a member of this league.",
      rateLimited: "Slow down — try again in a moment.",
    },
    status: {
      complete: "Draft complete",
      completeEyebrow: "Done",
    },
    filters: {
      kind: "Type",
      all: "All",
      pitchers: "Pitchers",
      hitters: "Hitters",
    },
  },
  player: {
    kind: {
      short: {
        pitcher: "P",
        positionPlayer: "H",
      },
      long: {
        pitcher: "Pitcher",
        positionPlayer: "Hitter",
      },
    },
    slot: {
      hitter: "H",
      pitcher: "P",
    },
  },
  roster: {
    slotLabel: "{kind}{index}",
  },
  freeAgents: {
    filters: {
      kind: "Type",
      all: "All",
      pitchers: "Pitchers",
      hitters: "Hitters",
    },
  },
  admin: {
    printRoster: "Print roster (PDF)",
    printRosterTitle: "ONPOINT — Rosters",
    printRosterGeneratedAt: "Generated {date}",
    shell: {
      section: "Administración",
      operatorConsole: "Operator console",
      adminConsole: "Admin console",
      navigation: "Admin navigation",
      sections: "Admin sections",
      breadcrumb: "Breadcrumb",
      closeMenu: "Close menu",
      openMenu: "Open menu",
    },
    auth: {
      title: "Admin sign-in",
      subtitle: "Restricted to commissioners and operators.",
      signInError: "Could not sign in.",
    },
    teams: {
      title: "Teams",
      subtitle:
        "Clubs in the eligible pool. Click a row for the hitter roster and per-player stats.",
      searchPlaceholder: "Search teams…",
      detailSubtitle:
        "{count} hitters · totals aggregated across every eligible game with recorded stats.",
      loading: "...",
      columns: {
        name: "Team",
        shortName: "Short",
        hitters: "Hitters",
        games: "Games",
        points: "Points",
      },
    },
    leagues: {
      title: "Leagues",
      subtitle:
        "Draft order, pick history, roster changes, and standings for every created league.",
      loading: "...",
      notFound: "Not found",
      notFoundTitle: "League not found",
      notFoundDescription: "This league may have been deleted or the URL may be stale.",
      detailSubtitle: "{season} / {members} managers / {hitters} H / {pitchers} P",
      emptyTitle: "No leagues yet",
      emptyDescription: "Created leagues will appear here once managers join and drafts begin.",
      metrics: {
        leagues: "Leagues",
        drafts: "Drafts",
        managers: "Managers",
        picks: "Picks",
      },
      searchLabel: "Search leagues",
      searchPlaceholder: "League, manager, season, or status",
      noMatchTitle: "No matching leagues",
      noMatchDescription: "Clear the search term to return to the full admin view.",
      columns: {
        league: "League",
        status: "Status",
        managers: "Managers",
        drafts: "Drafts",
        picks: "Picks",
        leader: "Leader",
        lastPick: "Last pick",
        open: "Open",
      },
      pointsAbbr: "pts",
      noLeader: "No leader",
      noPicks: "No picks",
      open: "Open",
      openLeague: "Open {league}",
      states: {
        setup: "Setup",
        drafting: "Drafting",
        active: "Active",
        complete: "Complete",
      },
    },
    leagueDetail: {
      metrics: {
        managers: "Managers",
        drafts: "Drafts",
        picks: "Picks",
        leader: "Leader",
        noLeader: "No leader",
      },
      emptyDraft: {
        title: "No draft created",
        description: "The draft board will appear here after this league starts drafting.",
      },
      draft: {
        boardTitle: "Draft board",
        pickProgress: "{picked}/{total} picks",
        completedAt: "completed {date}",
        pickHistory: "Pick history",
        noOrder: "No draft order assigned.",
        order: "Order {order}",
        onClock: "On clock",
        auto: "Auto",
        teamUnknown: "Team unknown",
        noPicks: "No draftees yet.",
        roundAbbr: "R{round}",
        phases: {
          main: "Main draft",
          pitcherSupplemental: "Pitcher draft",
        },
        states: {
          pending: "Pending",
          active: "Active",
          paused: "Paused",
          complete: "Complete",
        },
        columns: {
          pick: "Pick",
          manager: "Manager",
          draftee: "Draftee",
          slot: "Slot",
          points: "Points",
        },
      },
      standings: {
        title: "Standings",
        empty: "No managers have joined.",
        games: "{count} games",
      },
      roster: {
        selectTeam: "Select a team to see its players.",
        points: "{points} points",
        empty: "No drafted players yet.",
        bench: "Bench",
      },
      transactions: {
        title: "Roster changes",
        subtitle: "Automatic pickup/drop audit log for this league.",
        teamFilter: "Team filter",
        allTeams: "All teams",
        empty: "No roster changes for this filter.",
        picked: "Picked",
        dropped: "Dropped",
        meta: "Pick #{pick} / Round {round} / Slot {slot}",
      },
    },
    players: {
      title: "Players",
      subtitle:
        "Set each player's primary type for roster slots. Fantasy points follow the actual hitting and pitching stat rows.",
      searchPlaceholder: "Search players…",
      teamLabel: "Team",
      kindLabel: "Type",
      teamAll: "All teams",
      kindAll: "All types",
      kinds: {
        positionPlayer: "Position player",
        pitcher: "Pitcher",
      },
      columns: {
        name: "Player",
        team: "Team",
        kind: "Type",
        games: "GP",
        points: "Points",
      },
      emptyTitle: "No players match",
      emptyDescription: "Try clearing filters or search text.",
      loading: "...",
      missingPlayer: "Missing player",
      notFoundTitle: "Player not found",
      notFoundDescription: "This player may have been deleted or the link is stale.",
    },
    audit: {
      backToPlayers: "Players",
      active: "Active",
      inactive: "Inactive",
      unknownTeam: "Unknown team",
      teamAudit: "{team} · stored scoring audit",
      hittingTitle: "Hitting game log",
      hittingSubtitle: "Raw batting stats, stored points, and current scoring math.",
      pitchingTitle: "Pitching game log",
      pitchingSubtitle: "Raw pitching stats, stored points, and current scoring math.",
      emptyTitle: "No logged games",
      emptyDescription: "This player has no hitter or pitcher stat rows yet.",
      totalPoints: "Total points",
      hitting: "Hitting",
      pitching: "Pitching",
      hGames: "H games",
      pGames: "P games",
      rows: "Rows",
      game: "Game",
      points: "Points",
      why: "Why",
      flags: "Flags",
      missingGame: "Missing game",
      ineligible: "Ineligible",
      notFinal: "Not final",
      enteredBy: "Entered {date} by {name}",
      nowPoints: "Now {points}",
      verified: "Verified",
      noScoringEvents: "No scoring events",
      gameReferenceMissing: "Game reference missing",
      gameLink: "game",
    },
    games: {
      title: "Games",
      subtitle:
        "Schedule games, enter box-score stats, and watch standings update live across every league.",
      schedule: "Schedule game",
      list: {
        empty: "No games scheduled yet.",
        emptyTitle: "Empty schedule",
      },
      columnDate: "Date",
      columnHome: "Home",
      columnAway: "Away",
      columnStatus: "Status",
      status: {
        scheduled: "Scheduled",
        inProgress: "In progress",
        final: "Final",
        cancelled: "Cancelled",
        missing: "Missing",
        filled: "Filled",
      },
      filters: {
        searchPlaceholder: "Search teams…",
        statusLabel: "Status",
        dateLabel: "Date",
        teamLabel: "Team",
        statusAll: "All statuses",
        teamAll: "All teams",
      },
      modal: {
        title: "Schedule a game",
        date: "Date",
        home: "Home team",
        away: "Away team",
        submit: "Schedule",
        cancel: "Cancel",
        sameTeam: "Home and away teams must be different.",
        submitFail: "Could not schedule game.",
      },
      entry: {
        eyebrow: "Box score",
        markFinal: "Mark final",
        unmarkFinal: "Unmark final",
        save: "Save",
        saveAndFinal: "Save & mark final",
        totalPoints: "Total fantasy points",
        submitFail: "Could not save stats.",
        dnp: "DNP",
        xbhInvalid: "XBH > H",
        unsavedRows: "{count} unsaved",
        playerHeader: "Player",
        decisionLabel: "Decision",
        noDecision: "None",
      },
      import: {
        openButton: "Import from KNBSB",
        title: "Import box score from KNBSB",
        urlPlaceholder: "https://stats.knbsbstats.nl/.../box-score/...",
        urlLabel: "KNBSB box-score URL",
        fetch: "Fetch",
        fetchFail: "Could not fetch the box score.",
        matched: "Matched ({count})",
        matchedEmpty: "No matched players yet.",
        unmatched: "Unmatched ({count})",
        unmatchedEmpty: "Everything matched.",
        playerHeader: "Player",
        pitcherHeader: "Pitcher",
        confidence: "{pct}% match",
        unlink: "Unlink",
        linkPlaceholder: "Pick a player…",
        noTeam: "No team",
        addPlayer: "Add player",
        addPlayerFail: "Could not add player.",
        applyFail: "Could not apply stats.",
        otherTeams: "Other teams",
        apply: "Apply ({count})",
        applyBoth: "Apply ({hitters} hitters, {pitchers} pitchers)",
        pitchersMatched: "Pitchers matched ({count})",
        pitchersMatchedEmpty: "No pitcher rows matched yet.",
        pitchersUnmatched: "Pitchers unmatched ({count})",
        pitchersUnmatchedEmpty: "All pitcher rows resolved.",
        pitchersSaved: "Saved {count} pitcher line(s).",
        cancel: "Cancel",
      },
      bulkImport: {
        openButton: "Bulk KNBSB import",
        title: "Bulk import from KNBSB",
        urlPlaceholder: "Schedule page or box-score URL",
        dateLabel: "Game date",
        addUrl: "Add",
        preview: "Preview",
        removeUrl: "Remove {url}",
        fetchFail: "Could not fetch KNBSB games.",
        skipped: "{count} unfinished games skipped.",
        matchCounts: "{matched} matched / {unmatched} unmatched",
        pitchCounts: "P: {matched} matched / {unmatched} unmatched",
        noStatus: "No status",
        newGame: "New",
        hasStats: "Has stats",
        imported: "Imported",
        confirm: "Import this game",
        confirmAll: "Import all games ({count})",
        replaceAll: "Replace all stats ({count})",
        importReplaceAll: "Import / replace all ({count})",
        importProgress: "Importing {current}/{total} · {game}",
        importFail: "Could not import this game.",
        replace: "Replace stats",
      },
      todayImport: {
        button: "IMPORT today",
        success: "Imported {games} today game(s). Added {players} player(s).",
        partial: "Imported {imported} today game(s), {failed} failed. Added {players} player(s).",
        empty: "No final KNBSB games to import today. Skipped {skipped} unfinished game(s).",
        fail: "Could not import today's KNBSB games.",
      },
      errors: {
        gameHasStats: "Cannot delete a game with stats already entered.",
        invalidStatLine: "Stat line is invalid (extra-base hits exceed total hits).",
        gameNotEligible: "This game is not eligible for stat entry.",
      },
      headers: {
        AB: "AB",
        H: "H",
        D2B: "2B",
        D3B: "3B",
        HR: "HR",
        RBI: "RBI",
        R: "R",
        BB: "BB",
        SB: "SB",
        CS: "CS",
        SO: "SO",
        PTS: "PTS",
        IP: "IP",
        ER: "ER",
        DEC: "DEC",
      },
    },
    analytics: {
      title: "Analyse",
      subtitle: "Season and weekly leaderboards across every drafted player.",
      tabs: {
        all: "All",
        hitters: "Hitters",
        pitchers: "Pitchers",
      },
      sections: {
        seasonLeaders: "Season leaders",
        disciplineCold: "Discipline & cold lists",
        formInsights: "Form & insights",
        thisWeek: "This week",
        teams: "Teams",
      },
      leaderboards: {
        topSeason: "Top performers (season)",
        bottomSeason: "Bottom performers (season)",
        topWeek: "Top performers this week",
        topWeekAll: "Top performers this week — All",
        topWeekHitters: "Top hitters this week",
        topWeekPitchers: "Top pitchers this week",
        pitcherTopSeason: "Top pitchers (season)",
        pitcherWorstSeason: "Bottom pitchers (season)",
        wins: "Wins leaders",
        pitcherK: "Strikeout leaders",
        ip: "Innings leaders",
        era: "ERA leaders",
        whip: "WHIP leaders",
        k9: "K/9 leaders",
        bb9: "BB/9 (wildness)",
        pitcherRecentForm: "Pitcher recent form",
        pitcherHotStreaks: "Pitcher hot streaks",
        pitcherColdStreaks: "Pitcher cold streaks",
        pitcherConsistency: "Most consistent pitchers",
        pitcherBestGames: "Best pitching performances",
        pitcherRisers: "Pitcher risers",
        pitcherFallers: "Pitcher fallers",
        hr: "HR leaders",
        rbi: "RBI leaders",
        sb: "SB leaders",
        avg: "AVG leaders",
        hits: "Hits leaders",
        runs: "Runs leaders",
        bb: "Walks leaders",
        tb: "Total bases leaders",
        xbh: "Extra-base hits leaders",
        so: "Strikeouts (most)",
        obp: "OBP leaders",
        slg: "SLG leaders",
        ops: "OPS leaders",
        kRate: "K-rate leaders",
        bbRate: "BB-rate leaders",
        recentForm: "Recent form",
        hotStreaks: "Hot streaks",
        coldStreaks: "Cold streaks",
        consistency: "Most consistent",
        bestGames: "Best single-game performances",
        risers: "Week risers",
        fallers: "Week fallers",
        teamScoring: "Team scoring totals",
        teamTopPerformers: "Top performers by team",
      },
      teamFilter: {
        all: "All teams",
        label: "Team",
      },
      columns: {
        rank: "#",
        player: "Player",
        team: "Team",
        games: "GP",
        points: "PTS",
        value: "Value",
        opp: "Opp",
        date: "Date",
        line: "Line",
        through: "Through",
        streak: "Streak",
        mean: "Mean",
        stdDev: "σ",
        priorWeek: "Prior",
        currentWeek: "Current",
        delta: "Δ",
        players: "Players",
        fpPerGame: "FP/G",
        totalFp: "Total FP",
        ip: "IP",
        era: "ERA",
        whip: "WHIP",
        k9: "K/9",
        bb9: "BB/9",
        wins: "W",
        k: "K",
        er: "ER",
      },
      eyebrow: {
        minGames: "Min {count} games",
        minAb: "Min {count} AB",
        minIp: "Min {count} IP",
        week: "Week {week}",
        lastN: "Last {count} games",
        weekDelta: "Δ vs prior week",
      },
      empty: "Not enough at-bats yet.",
    },
    scoring: {
      title: "Point system",
      subtitle:
        "How fantasy points are awarded for each batting event. Read-only — share with participants.",
      version: "Ruleset v{version}",
      positive: "Positive scoring",
      penalties: "Penalties",
      columns: {
        rule: "Event",
        abbr: "Abbr",
        points: "Points",
      },
      rules: {
        single: "Single",
        double: "Double",
        triple: "Triple",
        homeRun: "Home run",
        rbi: "Run batted in",
        run: "Run scored",
        walk: "Walk",
        stolenBase: "Stolen base",
        caughtStealing: "Caught stealing",
        strikeout: "Strikeout",
      },
      pitcherTitle: "Pitcher point system",
      pitcherSubtitle:
        "How fantasy points are awarded for each pitching event. Any rostered player with pitching stats earns these points.",
      pitcherVersion: "Pitching ruleset v{version}",
      pitcherNote:
        "Innings are stored as outs (3 outs = 1 IP); display rounds 0/1/2 outs as the .0/.1/.2 KNBSB convention. Decisions (W/L) are parsed from the box score.",
      pitcherRules: {
        perOut: "Out recorded (1 IP = 3 outs)",
        perStrikeout: "Strikeout",
        perWin: "Win",
        perSave: "Save",
        perCompleteGame: "Complete game",
        perShutout: "Shutout",
        perEarnedRun: "Earned run",
        perWalk: "Walk allowed",
        perHitAllowed: "Hit allowed",
        perLoss: "Loss",
      },
      note: "Total points for a player are the sum of every event across all games. The ruleset is locked per league at creation and never changes mid-season.",
      maintenance: "Maintenance",
      saving: "Saving…",
      saved: "Saved. Run Update all stats to apply to existing stats.",
      savedHint: "Run Update all stats to apply to existing stats.",
      rescoreButton: "Update all stats",
      rescoreRunning: "Re-scoring…",
      rescoreConfirm:
        "Re-apply the current hitter and pitcher rules to every existing stat row, recompute season totals, and refresh derived historical profile scoring?",
      rescoreDone: "Re-scored {count} hitter and pitcher stat rows.",
      rescoreHint:
        "Run this after editing point values so games, standings, weekly totals, pitcher bonuses, and historical profile scores all use the same rules.",
      dangerZone: "Danger zone",
      purgeTitle: "Purge all stats",
      purgeSubtitle:
        "Deletes every entered box-score line and resets every manager's season total to zero. Drafts, leagues, players, and games are kept. Useful when starting the season fresh.",
      purgeButton: "Purge all stats",
      purgeRunning: "Purging…",
      purgeConfirm1:
        "This deletes ALL entered box-score lines and zeroes every manager's season total. Continue?",
      purgeConfirm2: "Last chance — this cannot be undone. Really purge all stats?",
      purgeDone: "Deleted {stats} stat rows · reset {totals} manager totals.",
    },
    cutPicks: {
      title: "Cut last X picks per manager",
      subtitle:
        "Removes each manager's most-recent picks (highest pick numbers) from a league. Use this if the draft made too many rounds and you want to shrink rosters before the season starts.",
      leagueLabel: "League",
      leaguePlaceholder: "Choose a league…",
      rosterTag: "{members} mgrs · roster {size}",
      countLabel: "Cut count",
      adjustLabel: "Reduce roster size to match",
      button: "Cut last {count}",
      running: "Cutting…",
      confirm1: "Drop the last {count} pick(s) from every manager in “{league}”?",
      confirm2: "Last chance — this cannot be undone. Continue?",
      done: "Removed {picks} picks across {members} managers · roster size now {roster}.",
    },
  },
} as const;

export type Dictionary = Widen<typeof enSource>;
export const en: Dictionary = enSource;

const es: Dictionary = {
  app: {
    name: "KNBSB Fantasy",
    tagline: "Hoofdklasse · Béisbol Fantasy",
  },
  nav: {
    home: "Inicio",
    league: "Liga",
    settings: "Ajustes",
    more: "Más",
    moreEmpty: "Abre una liga para ver sus secciones aquí.",
    primary: "Principal",
    leagueMenu: "Menú de liga",
    commandDeck: "Panel de mando",
    joinLeagueToUnlock: "Únete a una liga para desbloquear la navegación de liga.",
    menuItems: "{count} elementos del menú",
    signOut: "Cerrar sesión",
    language: "Idioma",
  },
  locale: {
    en: "English",
    es: "Español",
    nl: "Nederlands",
  },
  auth: {
    signInTitle: "Bienvenido de nuevo",
    signInSubtitle: "Inicia sesión para gestionar tu liga.",
    signUpTitle: "Crea tu cuenta",
    signUpSubtitle: "Tardarás aproximadamente un minuto.",
    email: "Correo electrónico",
    password: "Contraseña",
    confirmPassword: "Confirmar contraseña",
    showPassword: "Mostrar contraseña",
    hidePassword: "Ocultar contraseña",
    name: "Nombre completo",
    signIn: "Iniciar sesión",
    signUp: "Crear cuenta",
    newHere: "¿Eres nuevo aquí?",
    alreadyHaveOne: "¿Ya tienes una cuenta?",
    createOne: "Crear una",
    signInLink: "Iniciar sesión",
    errorGeneric: "Algo salió mal. Inténtalo de nuevo.",
  },
  home: {
    welcome: "Bienvenido, {name}",
    homeBase: "Tu base de béisbol fantasy.",
    getStarted: "Empezar",
    noLeagueYet: "Aún no tienes liga",
    noLeagueYetBody: "Crea una liga o únete a una con un código de invitación.",
    onboardTitle: "Una liga, una temporada.",
    onboardLead:
      "Crea una liga privada para tus amigos o únete a una con un código de invitación. La Hoofdklasse, a tu manera.",
    createLeague: "Crear una liga",
    createLeagueDesc: "Serás el comisionado. Decide las reglas e invita al resto.",
    joinLeague: "Únete con un código",
    joinLeagueDesc: "¿Tienes un código de 8 caracteres? Pégalo y elige el nombre de tu equipo.",
    or: "o",
  },
  league: {
    overview: "Resumen de la liga",
    season: "Temporada {year}",
    managers: "Mánagers",
    managersOf: "{count} de {max}",
    rosterSize: "Plantillas de {count} jugadores",
    inviteCode: "Código de invitación",
    inviteCodeHelp: "Compártelo con tus amigos. Cualquiera con el código puede unirse.",
    copy: "Copiar",
    copied: "Copiado",
    regenerate: "Regenerar",
    state: {
      setup: "Preparación",
      drafting: "Draft",
      active: "Activa",
      complete: "Finalizada",
    },
    commissioner: "Comisionado",
    startDraft: "Iniciar el draft",
    needTwo: "Se requieren al menos dos mánagers",
    yourTeam: "Tu equipo",
    activity: {
      eyebrow: "Feed de la liga",
      title: "Cambios de roster",
      open: "Abrir cambios de roster",
      close: "Cerrar cambios de roster",
      loading: "Cargando cambios de roster...",
      empty: "Aún no hay cambios de roster.",
      clearedEmpty: "Estás al día. Muestra el historial para revisar cambios anteriores.",
      clear: "Limpiar",
      clearing: "Limpiando...",
      showHistory: "Mostrar historial",
      showLatest: "Mostrar recientes",
      rowTitle: "{team} fichó a {player}",
      rowMeta: "Soltó a {dropped}",
      tradeRowTitle: "{team} traspasó por {player}",
      tradeRowMeta: "Envió a {sent}",
    },
    trades: {
      title: "Traspasos",
      subtitle: "Ofrece un intercambio uno por uno y deja que el otro mánager acepte o rechace.",
      newTrade: "Nuevo traspaso",
      incoming: "Recibidos",
      outgoing: "Enviados",
      history: "Historial",
      emptyIncoming: "No hay ofertas recibidas.",
      emptyOutgoing: "No hay ofertas enviadas.",
      emptyHistory: "Aún no hay traspasos resueltos.",
      offerLine: "{from} ofrece {offered} por {requested}",
      expires: "Expira {date}",
      resolved: "Resuelto {date}",
      accept: "Aceptar",
      decline: "Rechazar",
      cancel: "Cancelar",
      accepting: "Aceptando...",
      declining: "Rechazando...",
      cancelling: "Cancelando...",
      proposeTitle: "Proponer traspaso",
      proposeSubtitle: "Elige a quién quieres y qué jugador ofreces.",
      chooseManager: "Elegir mánager",
      requestedPlayer: "Jugador solicitado",
      offeredPlayer: "Tu oferta",
      chooseRequested: "Elige un jugador para pedir",
      chooseOffered: "Elige uno de los tuyos",
      summary: "Revisión",
      youReceive: "Recibes",
      youSend: "Envías",
      back: "Atrás",
      confirm: "Confirmar traspaso",
      submit: "Enviar oferta",
      sending: "Enviando...",
      unavailable: "Los traspasos están disponibles tras el draft mientras la liga está activa.",
      openLimit: "{count} ofertas abiertas permitidas",
      proposeFromRoster: "Ofrecer traspaso",
      statuses: {
        pending: "Pendiente",
        accepted: "Aceptado",
        declined: "Rechazado",
        cancelled: "Cancelado",
        expired: "Expirado",
        superseded: "Cerrado",
      },
      created: "Oferta de traspaso enviada.",
      accepted: "Traspaso aceptado.",
      declined: "Traspaso rechazado.",
      cancelled: "Traspaso cancelado.",
      failed: "La accion de traspaso fallo.",
    },
    activeRoster: {
      eyebrow: "Plantilla activa",
      title: "Tamaño de plantilla activa",
      description:
        "Cuántos jugadores drafteados de cada mánager cuentan para la clasificación. Plantilla: {roster}.",
      fieldLabel: "Activos",
      allCount: "Todos los jugadores drafteados cuentan para el total de temporada.",
      partialCount: "Top {active} suman · {bench} en el banco.",
      save: "Guardar",
      saving: "Guardando…",
      invalid: "El número activo debe estar entre 1 y {roster}.",
    },
    manage: {
      title: "Ajustes de la liga",
      description:
        "Edita los detalles o elimina la liga por completo. Se bloquea cuando empieza el draft.",
      detailsTitle: "Detalles",
      maxManagersHint: "Ya unidos: {count}.",
      edit: "Editar",
      save: "Guardar cambios",
      cancel: "Cancelar",
      remove: "Quitar",
      removeMember: "Quitar a {team} de la liga",
      scoringStart: "Inicio de puntuacion",
      scoringStartAll: "Toda la temporada",
      scoringStartTitle: "Ventana de puntuacion",
      scoringStartDescription:
        "Los partidos anteriores a esta fecha no cuentan para esta liga, pero siguen disponibles para otras ligas.",
      clearScoringStart: "Limpiar",
      draftTitle: "Control del draft",
      reset: "Reiniciar draft",
      resetConfirm: "Sí, reiniciar",
      resetDescription:
        "Cancela el draft actual y devuelve la liga a configuración. Todos los picks se descartan.",
      dangerZone: "Zona de peligro",
      deleteDescription:
        "Elimina permanentemente la liga, los mánagers y cualquier pick del draft.",
      delete: "Eliminar liga",
      deleteConfirm: "Sí, eliminar",
      pitcherDraft: {
        title: "Draft de lanzadores",
        description:
          "Añade huecos de lanzador a esta liga y haz un snake draft solo de lanzadores. La ronda 1 se invierte respecto al draft original (el peor equipo de bateo elige primero).",
        slotsLabel: "Huecos de lanzador",
        slotsHint: "1–10. La plantilla total crece en esa cantidad.",
        setSlots: "Guardar huecos",
        start: "Iniciar draft de lanzadores",
        setFirst: "Define los huecos de lanzador primero.",
        slotsSaved: "Huecos de lanzador establecidos en {count}.",
        invalidSlots: "Los huecos de lanzador deben ser un número entero entre 1 y 10.",
      },
      endLeague: {
        title: "Finalizar liga",
        description:
          "Cierra la clasificación y congela la liga. Los fichajes y los cambios de puntos por estadísticas se detienen para esta liga. No se puede deshacer.",
        confirm: "Finalizar liga",
        confirmHint: "Escribe el nombre de la liga para confirmar.",
        success: "Liga finalizada.",
      },
      ended: "Liga finalizada el {date}",
      seasonEnd: {
        title: "Fin de temporada",
        description:
          "Cuando pase esta fecha y todos los partidos programados hasta ese día estén finales, la liga se cierra automáticamente.",
        fieldLabel: "Fecha de fin de temporada",
        clear: "Borrar",
      },
    },
    games: {
      title: "Partidos",
      subtitle: "Partidos",
      empty: "Aún no hay partidos esta temporada.",
      emptyTitle: "Calendario tranquilo",
      comingSection: "Próximos partidos",
      previousSection: "Partidos anteriores",
      columnDate: "Fecha",
      columnHome: "Local",
      columnAway: "Visitante",
      detail: {
        eyebrow: "Resumen estadístico",
        backToList: "Todos los partidos",
        yourPlayer: "Tu jugador",
        draftedBy: "Drafteado por {team}",
        youScored: "Sumaste {points} puntos en este partido",
        nonePlayed: "Ninguno de tus jugadores apareció en este partido.",
        noStatsYet: "Aún no hay estadísticas para este partido.",
        showOnlyMine: "Solo mis jugadores",
        showAll: "Todos",
      },
      headers: {
        AB: "AB",
        H: "H",
        D2B: "2B",
        D3B: "3B",
        HR: "HR",
        RBI: "RBI",
        R: "R",
        BB: "BB",
        SB: "SB",
        CS: "CS",
        SO: "SO",
        PTS: "PTS",
        IP: "IP",
        ER: "ER",
        DEC: "DEC",
      },
    },
    standings: {
      title: "Clasificación",
      subtitle: "Dónde está cada equipo, actualizado en vivo con cada box score.",
      empty: "Aún no hay puntos — espera el primer box score.",
      emptyTitle: "Tabla vacía",
      regular: "Temporada regular",
      champion: "Campeón",
      columnRank: "#",
      columnTeam: "Equipo",
      columnPoints: "PTS",
      columnGames: "J",
      youLabel: "Tú",
      weeklyChart: {
        title: "Puntos por semana",
        subtitle: "Puntos de la temporada por semana.",
        empty: "Aún no hay suficientes datos semanales para mostrar.",
      },
      topPlayers: {
        title: "Mejores actuaciones",
        subtitle: "Los jugadores con más puntos de toda la liga.",
        seeAll: "Ver todos los jugadores",
        empty: "Aún no hay estadísticas de jugadores.",
      },
    },
    scoring: {
      title: "Puntuación",
      subtitle:
        "Cómo se otorgan los puntos de fantasy. Solo lectura — definido por el comisionado.",
      hitterTitle: "Bateo",
      pitcherTitle: "Lanzamiento",
      columns: {
        rule: "Evento",
        abbr: "Abr",
        points: "Puntos",
      },
    },
    players: {
      title: "Jugadores",
      subtitle: "Todos los jugadores drafteados ordenados por puntos fantasy en la temporada.",
      empty: "Aún no hay estadísticas de jugadores drafteados.",
      emptyTitle: "Tabla vacía",
      columnRank: "#",
      columnPlayer: "Jugador",
      columnTeam: "Equipo KNBSB",
      columnDrafter: "Drafteado por",
      columnGames: "J",
      columnPoints: "PTS",
      freeAgents: {
        title: "Agentes libres",
        subtitle:
          "Jugadores no drafteados ordenados por puntos. Recoge a uno para meterlo en tu plantilla.",
        empty: "No hay jugadores no drafteados en el grupo elegible.",
        searchPlaceholder: "Buscar jugador o equipo",
        noMatch: "Ningún jugador coincide con la búsqueda.",
        pickUp: "Recoger",
        picking: "Recogiendo…",
        columnMove: "Movimiento",
        pickUpPlayer: "Recoger jugador",
        pickUpName: "Recoger a {name}",
        replaceHint:
          "Elige el espacio de la plantilla que quieres reemplazar. El jugador entrante hereda ese pick y los totales de temporada se recalculan de inmediato.",
        noCompatibleSlots: "No hay espacios compatibles disponibles en la plantilla.",
        dismissError: "Cerrar error",
      },
      allPlayers: {
        title: "Todos los drafteados",
        subtitle:
          "Todos los jugadores actualmente en plantilla en esta liga, ordenados por puntos.",
        empty: "Aún no se ha drafteado a ningún jugador.",
        columnTeam: "Equipo",
      },
      profile: {
        title: "Perfil del jugador",
        notFound: "Jugador no encontrado",
        noLongerAvailable: "Este jugador ya no está disponible en los datos de la liga.",
        backToPlayers: "Jugadores",
        unknownPlayer: "Jugador desconocido",
        team: "Equipo",
        fantasy: "Equipo fantasy",
        freeAgent: "Agente libre",
        undrafted: "Sin draftear",
        unassigned: "Sin asignar",
        noCompetition: "Sin competencia",
        pickMeta: "Elección {pick}",
        gameLogs: "Registros de partidos",
        scoringRun: "Racha de puntuación",
        noGameLogs: "Todavía no hay registros de partidos para este jugador.",
        hitting: "Bateo",
        pitching: "Lanzamiento",
        viewGameAria: "Abrir detalles del partido contra {opponent} el {date}",
      },
      dropPickup: {
        title: "Sustituye a un tuyo por {name}",
        titleFallback: "Sustituir uno de los tuyos",
        subtitle:
          "Elige al jugador que sueltas. Los puntos de temporada del nuevo jugador cuentan para ti de inmediato.",
        subtitlePitcher:
          "Elige al pitcher que sueltas. Los puntos de temporada del nuevo jugador cuentan para ti de inmediato.",
        subtitleHitter:
          "Elige al bateador que sueltas. Los puntos de temporada del nuevo jugador cuentan para ti de inmediato.",
        pickN: "Elección {n}",
        noRoster: "No tienes jugadores drafteados que sustituir.",
        noPitchers: "No tienes pitchers que sustituir.",
        noHitters: "No tienes bateadores que sustituir.",
        cancel: "Cancelar",
      },
    },
    profile: {
      backToStandings: "Toda la clasificación",
      eyebrow: "Mánager",
      stats: {
        regularPoints: "Total",
        regularGames: "G temporada regular",
      },
      roster: {
        title: "Plantilla",
        subtitle: "Ordenada por puntos fantasy en la temporada.",
        empty: "No hay jugadores drafteados.",
        columnPick: "Elección",
        columnPlayer: "Jugador",
        columnTeam: "Equipo",
        columnGames: "J",
        columnRegular: "Temp.",
        columnTotal: "Acumulado",
        autoBadge: "Automático",
      },
      recent: {
        title: "Historial de aportes",
        subtitle:
          "Últimas líneas de puntuación de la plantilla. Abre la vista completa de antiguo a nuevo.",
        empty: "Aún no hay contribuciones en partidos.",
        columnDate: "Fecha",
        columnPlayer: "Jugador",
        columnGame: "Partido",
        columnPoints: "PTS",
        backToManager: "Perfil del mánager",
        historyTitle: "Aportes",
      },
    },
    dashboard: {
      lastGame: "Último partido",
      noLastGame: "Aún no se han disputado partidos.",
      top3: "Mejores 3",
      rank: "Posición",
      points: "Puntos",
      pointsAbbr: "puntos",
      finalStandingsTitle: "Clasificación final",
      finalStandingsEndedOn: "Temporada terminada el {date}",
      seeStandings: "Ver clasificación completa",
      yourRoster: "Tu plantilla",
      yourRosterSubtitle: "Forma en los tres últimos partidos de cada jugador.",
      rosterEmpty: "El draft aún no ha terminado.",
      columnLast3: "Últimos 3",
      columnForm: "Forma",
      columnL3: "U3",
      columnLastThreePoints: "Últ. 3",
      columnSeason: "Temporada",
      columnTotal: "Total",
      lastThreeGames: "Últimos tres partidos",
      formNoGame: "—",
      yourEdge: "Tu ventaja",
      atTheTop: "Cabeza de la tabla",
      gapToLeader: "{points} del líder",
      aboveCutline: "{points} sobre el corte",
      belowCutline: "{points} bajo el corte",
      cutlineFirm: "Aguantas el corte por {points}",
      hottest: "Caliente · {name}",
      coldest: "Frío · {name}",
      noForm: "Aún no hay forma reciente.",
      benchDivider: "Banco · {count} jugadores no cuentan",
      contributions: {
        title: "Aportes recientes",
        subtitle: "Las últimas líneas de partido de tu plantilla — más recientes primero.",
        sentence: "{player} aportó a {team} con {points}",
        statLine: "Línea {kind}",
        empty: "Aún no hay aportes en partidos.",
        seeAll: "Ver todo",
        pageTitle: "Aportes recientes",
        pageSubtitle:
          "Cada partido elegible en el que tu plantilla ha puntuado, más recientes primero.",
        columnDate: "Fecha",
        columnPlayer: "Jugador",
        columnTeam: "Equipo",
        columnOpponent: "Riv",
        columnPoints: "PTS",
      },
      gameLog: {
        openAria: "Abrir registro de partidos de {name}",
        subtitle: "Todos los partidos elegibles en los que ha puntuado. Más recientes primero.",
        empty: "Aún no ha puntuado en ningún partido.",
        hittingSection: "Bateo",
        pitchingSection: "Pitcheo",
        totals: "Totales · {count} P",
        columnDate: "Fecha",
        columnOpponent: "Riv",
        colAB: "AB",
        colR: "C",
        colH: "H",
        col2B: "2B",
        col3B: "3B",
        colHR: "HR",
        colRBI: "CI",
        colBB: "BB",
        colSB: "BR",
        colK: "K",
        colFP: "PF",
        colIP: "IP",
        colER: "CL",
        colHA: "H",
        colBBA: "BB",
        colKA: "K",
        colDec: "Decisión",
      },
    },
  },
  create: {
    title: "Crea tu liga",
    subtitle:
      "Define las reglas. Cuando empiece el draft, los ajustes se bloquean por la temporada.",
    leagueName: "Nombre de la liga",
    teamName: "Nombre de tu equipo",
    season: "Temporada",
    maxManagers: "Mánagers máximos",
    rosterSize: "Tamaño de la plantilla",
    hitterSlots: "Bateadores por equipo",
    pitcherSlots: "Lanzadores por equipo",
    totalRoster: "Tamaño total de la plantilla: {total}",
    pickTimer: "Temporizador de selección (segundos)",
    draftTimerEnabled: "Activar temporizador de selección",
    draftTimerEnabledHelp: "El comisionado marca el ritmo. Cada mánager elige cuando pueda.",
    pickClock: "Reloj de selección",
    pickClockTimed: "Con tiempo",
    pickClockUntimed: "Sin tiempo",
    scoringStart: "Inicio de puntuación",
    scoringStartHelp:
      "Opcional. Los partidos previos a esta fecha no cuentan para esta liga. Déjalo en blanco para contar todos los partidos elegibles.",
    capacityOk:
      "Capacidad OK — {picks} elecciones de {pool} jugadores elegibles ({buffer}% margen)",
    capacityFail: "La verificación de capacidad falló.",
    submit: "Crear liga",
    submitFail: "No se pudo crear la liga.",
  },
  join: {
    title: "Unirse a una liga",
    subtitle: "¿Tienes un código del comisionado? Pégalo aquí.",
    inviteCode: "Código de invitación",
    teamName: "Nombre de tu equipo",
    submit: "Unirse a la liga",
    submitFail: "No se pudo unir a la liga.",
  },
  settings: {
    title: "Ajustes",
    subtitle: "Cuenta, idioma y configuración por liga.",
    signedInAs: "Sesión iniciada como",
    signOut: "Cerrar sesión",
    language: "Idioma",
    languageHelp: "Elige cómo te habla la app.",
    tabs: {
      account: "Cuenta",
      leagues: "Ligas",
      language: "Idioma",
    },
    leagues: {
      empty: {
        title: "Aún no hay ligas",
        body: "Crea o únete a una liga desde el inicio para gestionarla aquí.",
      },
    },
  },
  validation: {
    required: "Obligatorio",
  },
  errors: {
    routeEyebrow: "Error de ruta",
    routeTitle: "No se pudo cargar esta pantalla",
    generic: "Algo salió mal.",
    tryAgain: "Intentar de nuevo",
  },
  ui: {
    dataTable: {
      noRows: "No hay filas.",
      pageSize: "Tamaño de página",
      page: "Página",
      of: "de",
      prev: "Anterior",
      next: "Siguiente",
      search: "Buscar",
      clear: "Limpiar",
      rangeFrom: "{label} desde",
      rangeTo: "{label} hasta",
    },
    datePicker: {
      selectDate: "Selecciona una fecha",
      selectDateTime: "Selecciona fecha y hora",
      pickRange: "Selecciona un rango",
      previousMonth: "Mes anterior",
      nextMonth: "Mes siguiente",
      time: "Hora",
      hour: "Hora",
      minute: "Minuto",
      clear: "Limpiar",
    },
    nightSearch: {
      placeholder: "Buscar jugadores...",
      filterPlayers: "Filtrar jugadores",
    },
  },
  pwa: {
    install: {
      title: "Instala Fantasy",
      body: "Añádela a tu pantalla de inicio para acceso instantáneo y experiencia nativa.",
      iosBody: "Toca el botón Compartir en Safari y elige 'Añadir a la pantalla de inicio'.",
      cta: "Instalar",
      dismiss: "Cerrar",
    },
    offline: {
      banner: "Estás sin conexión - mostrando últimos datos",
    },
    update: {
      title: "Actualización disponible",
      body: "Hay una nueva versión de Fantasy. Recarga para aplicarla.",
      cta: "Recargar",
      dismiss: "Más tarde",
    },
  },
  share: {
    button: "Compartir",
    failed: "No se pudo generar la tarjeta para compartir.",
  },
  draft: {
    title: "Draft",
    onClock: {
      eyebrow: "En turno",
      yourTurn: "Tu pick",
      waitingFor: "Esperando a {team}",
      pickNumber: "Pick {pick} · Ronda {round}",
      pitcherPill: "SOLO LANZADORES",
    },
    timer: {
      secondsLeft: "{seconds} s",
      paused: "Pausado",
      pausedOff: "Pausado · temporizador apagado",
      expired: "Eligiendo automáticamente…",
      off: "Temporizador apagado",
    },
    autoPick: {
      toggleLabel: "Autoelegir mis favoritos (15s)",
      eyebrow: "Autoelección · 15s",
      body: "Desde mis favoritos, en orden",
      tapToEnable: "Toca para activar",
      needFavorites: "Marca al menos un jugador como favorito para activar esto.",
      firingFromFavorites: "Eligiendo automáticamente desde favoritos…",
      explainer: {
        title: "Cómo funciona la autoelección",
        body1:
          "Cuando el temporizador de la liga está apagado y es tu turno, tienes 15 segundos antes de que se active la autoelección.",
        body2:
          "Solo se eligen jugadores de tus favoritos, en orden. Si todos tus favoritos ya fueron drafteados, no se hace ningún pick y sigues en turno.",
        body3: "Puedes apagarlo cuando quieras.",
        confirm: "Entendido — activar",
        cancel: "Cancelar",
      },
    },
    available: {
      title: "Jugadores disponibles",
      searchPlaceholder: "Buscar jugadores",
      teamFilterAll: "Todos los equipos",
      favoritesChip: "Favoritos ({count})",
      empty: "Ningún jugador coincide.",
      emptyFavorites: "Ningún favorito coincide — marca un jugador con estrella para agregarlo.",
      pickButton: "Elegir",
      notYourTurn: "No es tu turno",
    },
    myPicks: {
      title: "Mis picks",
      countLabel: "{count} elecciones",
      openButton: "Mis picks ({count})",
      openLabel: "Mostrar mis picks",
      empty: "Todavía no has drafteado jugadores.",
      notAMember: "No eres miembro de esta liga.",
    },
    nextPicks: {
      label: "Sigue",
    },
    turnAlert: {
      title: "Es tu turno",
      body: "Estás en turno — ve a la pestaña Draft para hacer tu pick.",
      goToDraft: "Ir al Draft",
      dismiss: "Descartar",
    },
    history: {
      title: "Historial de picks",
      empty: "Aún no hay picks.",
      roundLabel: "Ronda {round}",
      autoBadge: "Automático",
      pickByManager: "{team} → {player}",
    },
    board: {
      title: "Tablero del draft",
      titlePitcher: "Tablero de draft de pitchers",
      onClockPill: "Eligiendo",
      notStarted: "El draft aún no ha empezado.",
    },
    tabs: {
      draft: "Draft",
      board: "Tablero",
    },
    queue: {
      title: "Mis favoritos",
      empty:
        "Marca jugadores con estrella para añadirlos a tus favoritos. El primero entra automáticamente si caduca tu turno.",
      addToQueue: "Añadir a favoritos",
      removeFromQueue: "Quitar de favoritos",
      clearAll: "Limpiar",
      topUp: "Siguiente",
      pickNow: "Elegir ahora",
      muteAlerts: "Silenciar alertas",
      unmuteAlerts: "Activar alertas",
      searchPlaceholder: "Filtrar favoritos",
      noMatch: "Ningún favorito coincide.",
      moveUp: "Subir",
      moveDown: "Bajar",
    },
    roster: {
      title: "Mi plantilla",
      subtitle: "Jugadores drafteados por {team} esta temporada.",
      empty: "No drafteaste a ningún jugador.",
    },
    controls: {
      start: "Iniciar draft",
      startPitcher: "Iniciar draft de lanzadores",
      startPitcherConfirm:
        "¿Iniciar el draft suplementario de lanzadores? El orden de la ronda 1 se invierte respecto al draft principal.",
      pause: "Pausar",
      resume: "Reanudar",
      startConfirm: "¿Iniciar el draft ahora?",
      timerOn: "Temporizador activo",
      timerOff: "Temporizador apagado",
      cancel: "Cancelar",
      disableTimerTitle: "¿Quitar el reloj de picks?",
      disableTimerBody:
        "El temporizador de la liga se detendrá. Los miembros con autoelección personal seguirán teniendo un reloj de 15s solo para favoritos; los demás seguirán en turno hasta elegir manualmente o hasta que los contactes.",
      disableTimerConfirm: "Sí, quitarlo",
      enableTimerTitle: "¿Restaurar el reloj de picks?",
      enableTimerBody:
        "El temporizador de la liga volverá a contar desde la duración completa del pick. Los relojes personales de autoelección se detienen — las autoelecciones usarán la selección aleatoria estándar de la liga cuando se agoten los favoritos de un miembro.",
      enableTimerConfirm: "Sí, restaurarlo",
    },
    errors: {
      notYourTurn: "No es tu turno.",
      alreadyDrafted: "Ese jugador ya ha sido seleccionado.",
      draftNotActive: "El draft no está activo.",
      slotFull: "Tus huecos para ese tipo de jugador están llenos.",
      notAMember: "Ya no eres miembro de esta liga.",
      rateLimited: "Más despacio — inténtalo de nuevo en un momento.",
    },
    status: {
      complete: "Draft completado",
      completeEyebrow: "Listo",
    },
    filters: {
      kind: "Tipo",
      all: "Todos",
      pitchers: "Lanzadores",
      hitters: "Bateadores",
    },
  },
  player: {
    kind: {
      short: {
        pitcher: "P",
        positionPlayer: "H",
      },
      long: {
        pitcher: "Lanzador",
        positionPlayer: "Bateador",
      },
    },
    slot: {
      hitter: "H",
      pitcher: "P",
    },
  },
  roster: {
    slotLabel: "{kind}{index}",
  },
  freeAgents: {
    filters: {
      kind: "Tipo",
      all: "Todos",
      pitchers: "Lanzadores",
      hitters: "Bateadores",
    },
  },
  admin: {
    printRoster: "Imprimir plantilla (PDF)",
    printRosterTitle: "ONPOINT — Plantillas",
    printRosterGeneratedAt: "Generado el {date}",
    shell: {
      section: "Admin",
      operatorConsole: "Consola de operador",
      adminConsole: "Consola admin",
      navigation: "Navegación admin",
      sections: "Secciones admin",
      breadcrumb: "Ruta de navegación",
      closeMenu: "Cerrar menú",
      openMenu: "Abrir menú",
    },
    auth: {
      title: "Inicio de sesión admin",
      subtitle: "Restringido a comisionados y operadores.",
      signInError: "No se pudo iniciar sesión.",
    },
    teams: {
      title: "Equipos",
      subtitle:
        "Clubes elegibles. Haz clic en una fila para ver la plantilla de bateadores y las estadísticas por jugador.",
      searchPlaceholder: "Buscar equipos…",
      detailSubtitle:
        "{count} bateadores · totales agregados de cada partido elegible con estadísticas registradas.",
      loading: "Cargando...",
      columns: {
        name: "Equipo",
        shortName: "Abrev.",
        hitters: "Bateadores",
        games: "Partidos",
        points: "Puntos",
      },
    },
    leagues: {
      title: "Ligas",
      subtitle:
        "Orden de draft, historial de selecciones, cambios de plantilla y posiciones de cada liga creada.",
      loading: "Cargando...",
      notFound: "No encontrada",
      notFoundTitle: "Liga no encontrada",
      notFoundDescription:
        "Esta liga puede haber sido eliminada o la URL puede estar desactualizada.",
      detailSubtitle: "{season} / {members} mánagers / {hitters} B / {pitchers} L",
      emptyTitle: "Aún no hay ligas",
      emptyDescription:
        "Las ligas creadas aparecerán aquí cuando se sumen mánagers y empiecen los drafts.",
      metrics: {
        leagues: "Ligas",
        drafts: "Procesos de draft",
        managers: "Mánagers",
        picks: "Selecciones",
      },
      searchLabel: "Buscar ligas",
      searchPlaceholder: "Liga, mánager, temporada o estado",
      noMatchTitle: "No hay ligas coincidentes",
      noMatchDescription: "Borra el término de búsqueda para volver a la vista admin completa.",
      columns: {
        league: "Liga",
        status: "Estado",
        managers: "Mánagers",
        drafts: "Drafts activos",
        picks: "Selecciones",
        leader: "Líder",
        lastPick: "Última selección",
        open: "Abrir",
      },
      pointsAbbr: "puntos",
      noLeader: "Sin líder",
      noPicks: "Sin selecciones",
      open: "Abrir",
      openLeague: "Abrir {league}",
      states: {
        setup: "Configuración",
        drafting: "En draft",
        active: "Activa",
        complete: "Completa",
      },
    },
    leagueDetail: {
      metrics: {
        managers: "Mánagers",
        drafts: "Procesos de draft",
        picks: "Selecciones",
        leader: "Líder",
        noLeader: "Sin líder",
      },
      emptyDraft: {
        title: "No se ha creado un draft",
        description: "El tablero del draft aparecerá aquí cuando esta liga empiece a seleccionar.",
      },
      draft: {
        boardTitle: "Tablero del draft",
        pickProgress: "{picked}/{total} selecciones",
        completedAt: "completado el {date}",
        pickHistory: "Historial de selecciones",
        noOrder: "No hay orden de draft asignado.",
        order: "Orden {order}",
        onClock: "En turno",
        auto: "Automático",
        teamUnknown: "Equipo desconocido",
        noPicks: "Aún no hay seleccionados.",
        roundAbbr: "Ronda {round}",
        phases: {
          main: "Draft principal",
          pitcherSupplemental: "Draft de lanzadores",
        },
        states: {
          pending: "Pendiente",
          active: "Activo",
          paused: "Pausado",
          complete: "Completo",
        },
        columns: {
          pick: "Selección",
          manager: "Mánager",
          draftee: "Seleccionado",
          slot: "Ranura",
          points: "Puntos",
        },
      },
      standings: {
        title: "Clasificación",
        empty: "No se ha unido ningún mánager.",
        games: "{count} partidos",
      },
      roster: {
        selectTeam: "Selecciona un equipo para ver sus jugadores.",
        points: "{points} puntos",
        empty: "Aún no hay jugadores seleccionados.",
        bench: "Banca",
      },
      transactions: {
        title: "Cambios de plantilla",
        subtitle: "Registro automático de altas y bajas de esta liga.",
        teamFilter: "Filtro de equipo",
        allTeams: "Todos los equipos",
        empty: "No hay cambios de plantilla para este filtro.",
        picked: "Agregó",
        dropped: "Soltó",
        meta: "Selección #{pick} / Ronda {round} / Ranura {slot}",
      },
    },
    players: {
      title: "Jugadores",
      subtitle:
        "Define el tipo de cada jugador. Los jugadores bidireccionales acumulan puntos como bateadores y como lanzadores, y son elegibles en cualquier ranura de plantilla.",
      searchPlaceholder: "Buscar jugadores…",
      teamLabel: "Equipo",
      kindLabel: "Tipo",
      teamAll: "Todos los equipos",
      kindAll: "Todos los tipos",
      kinds: {
        positionPlayer: "Jugador de posición",
        pitcher: "Lanzador",
      },
      columns: {
        name: "Jugador",
        team: "Equipo",
        kind: "Tipo",
        games: "PJ",
        points: "Puntos",
      },
      emptyTitle: "Ningún jugador coincide",
      emptyDescription: "Intenta borrar los filtros o el texto de búsqueda.",
      loading: "Cargando...",
      missingPlayer: "Jugador faltante",
      notFoundTitle: "Jugador no encontrado",
      notFoundDescription:
        "Este jugador puede haber sido eliminado o el enlace puede estar desactualizado.",
    },
    audit: {
      backToPlayers: "Jugadores",
      active: "Activo",
      inactive: "Inactivo",
      unknownTeam: "Equipo desconocido",
      teamAudit: "{team} · auditoría de puntuación guardada",
      hittingTitle: "Registro de juegos al bate",
      hittingSubtitle: "Stats de bateo en bruto, puntos guardados y cálculo de puntuación actual.",
      pitchingTitle: "Registro de juegos como lanzador",
      pitchingSubtitle:
        "Stats de pitcheo en bruto, puntos guardados y cálculo de puntuación actual.",
      emptyTitle: "Sin juegos registrados",
      emptyDescription: "Este jugador aún no tiene filas de bateo ni de pitcheo.",
      totalPoints: "Puntos totales",
      hitting: "Bateo",
      pitching: "Pitcheo",
      hGames: "Juegos bateo",
      pGames: "Juegos pitcheo",
      rows: "Filas",
      game: "Juego",
      points: "Puntos",
      why: "Detalle",
      flags: "Marcas",
      missingGame: "Juego faltante",
      ineligible: "No elegible",
      notFinal: "No final",
      enteredBy: "Ingresado {date} por {name}",
      nowPoints: "Ahora {points}",
      verified: "Verificado",
      noScoringEvents: "Sin eventos de puntuación",
      gameReferenceMissing: "Falta la referencia del juego",
      gameLink: "juego",
    },
    games: {
      title: "Partidos",
      subtitle:
        "Programa partidos, registra estadísticas del box score y mira las clasificaciones actualizarse en vivo en cada liga.",
      schedule: "Programar partido",
      list: {
        empty: "Aún no hay partidos programados.",
        emptyTitle: "Calendario vacío",
      },
      columnDate: "Fecha",
      columnHome: "Local",
      columnAway: "Visitante",
      columnStatus: "Estado",
      status: {
        scheduled: "Programado",
        inProgress: "En curso",
        final: "Finalizado",
        cancelled: "Cancelado",
        missing: "Falta",
        filled: "Cargado",
      },
      filters: {
        searchPlaceholder: "Buscar equipos…",
        statusLabel: "Estado",
        dateLabel: "Fecha",
        teamLabel: "Equipo",
        statusAll: "Todos los estados",
        teamAll: "Todos los equipos",
      },
      modal: {
        title: "Programar un partido",
        date: "Fecha",
        home: "Equipo local",
        away: "Equipo visitante",
        submit: "Programar",
        cancel: "Cancelar",
        sameTeam: "Los equipos local y visitante deben ser distintos.",
        submitFail: "No se pudo programar el partido.",
      },
      entry: {
        eyebrow: "Resumen estadístico",
        markFinal: "Marcar como final",
        unmarkFinal: "Desmarcar final",
        save: "Guardar",
        saveAndFinal: "Guardar y marcar final",
        totalPoints: "Puntos fantasy totales",
        submitFail: "No se pudieron guardar las estadísticas.",
        dnp: "NJ",
        xbhInvalid: "XBH mayor que H",
        unsavedRows: "{count} sin guardar",
        playerHeader: "Jugador",
        decisionLabel: "Decisión",
        noDecision: "Ninguna",
      },
      import: {
        openButton: "Importar desde KNBSB",
        title: "Importar box score de KNBSB",
        urlPlaceholder: "Pega una URL de box score de KNBSB",
        urlLabel: "URL de box score de KNBSB",
        fetch: "Obtener",
        fetchFail: "No se pudo obtener el box score.",
        matched: "Coincidencias ({count})",
        matchedEmpty: "Aún no hay jugadores coincidentes.",
        unmatched: "Sin coincidencia ({count})",
        unmatchedEmpty: "Todo cuadra.",
        playerHeader: "Jugador",
        pitcherHeader: "Lanzador",
        confidence: "{pct}% coincidencia",
        unlink: "Desvincular",
        linkPlaceholder: "Elegir jugador…",
        noTeam: "Sin equipo",
        addPlayer: "Añadir jugador",
        addPlayerFail: "No se pudo anadir el jugador.",
        applyFail: "No se pudieron aplicar las stats.",
        otherTeams: "Otros equipos",
        apply: "Aplicar ({count})",
        applyBoth: "Aplicar ({hitters} bateadores, {pitchers} lanzadores)",
        pitchersMatched: "Lanzadores coincidentes ({count})",
        pitchersMatchedEmpty: "Aún no hay líneas de lanzador coincidentes.",
        pitchersUnmatched: "Lanzadores sin coincidencia ({count})",
        pitchersUnmatchedEmpty: "Todas las líneas de lanzador resueltas.",
        pitchersSaved: "Se guardaron {count} línea(s) de lanzador.",
        cancel: "Cancelar",
      },
      bulkImport: {
        openButton: "Importación KNBSB en lote",
        title: "Importar en lote desde KNBSB",
        urlPlaceholder: "Página de calendario o URL de box score",
        dateLabel: "Fecha del juego",
        addUrl: "Añadir",
        preview: "Previsualizar",
        removeUrl: "Quitar {url}",
        fetchFail: "No se pudieron obtener los partidos de KNBSB.",
        skipped: "{count} partidos no finalizados omitidos.",
        matchCounts: "{matched} vinculados / {unmatched} sin vincular",
        pitchCounts: "P: {matched} vinculados / {unmatched} sin vincular",
        noStatus: "Sin estado",
        newGame: "Nuevo",
        hasStats: "Tiene stats",
        imported: "Importado",
        confirm: "Importar este partido",
        confirmAll: "Importar todos ({count})",
        replaceAll: "Reemplazar todas ({count})",
        importReplaceAll: "Importar / reemplazar ({count})",
        importProgress: "Importando {current}/{total} · {game}",
        importFail: "No se pudo importar este partido.",
        replace: "Reemplazar stats",
      },
      todayImport: {
        button: "IMPORTAR hoy",
        success: "Importados {games} partido(s) de hoy. Anadio {players} jugador(es).",
        partial:
          "Importados {imported} partido(s), {failed} fallaron. Anadio {players} jugador(es).",
        empty:
          "No hay partidos finales de KNBSB para importar hoy. Se omitieron {skipped} sin finalizar.",
        fail: "No se pudieron importar los partidos de KNBSB de hoy.",
      },
      errors: {
        gameHasStats: "No se puede eliminar un partido con estadísticas registradas.",
        invalidStatLine:
          "La línea de estadísticas no es válida (los hits extrabase superan los hits totales).",
        gameNotEligible: "Este partido no es elegible para registrar estadísticas.",
      },
      headers: {
        AB: "AB",
        H: "H",
        D2B: "2B",
        D3B: "3B",
        HR: "HR",
        RBI: "RBI",
        R: "R",
        BB: "BB",
        SB: "SB",
        CS: "CS",
        SO: "SO",
        PTS: "PTS",
        IP: "IP",
        ER: "ER",
        DEC: "DEC",
      },
    },
    analytics: {
      title: "Analítica",
      subtitle: "Tablas de líderes de la temporada y semanales de cada jugador drafteado.",
      tabs: {
        all: "Todos",
        hitters: "Bateadores",
        pitchers: "Lanzadores",
      },
      sections: {
        seasonLeaders: "Líderes de la temporada",
        disciplineCold: "Disciplina y peores",
        formInsights: "Forma e insights",
        thisWeek: "Esta semana",
        teams: "Equipos",
      },
      leaderboards: {
        topSeason: "Mejores rendimientos (temporada)",
        bottomSeason: "Peores rendimientos (temporada)",
        topWeek: "Mejores rendimientos esta semana",
        topWeekAll: "Mejores esta semana — Todos",
        topWeekHitters: "Mejores bateadores esta semana",
        topWeekPitchers: "Mejores lanzadores esta semana",
        pitcherTopSeason: "Mejores lanzadores (temporada)",
        pitcherWorstSeason: "Peores lanzadores (temporada)",
        wins: "Líderes en victorias",
        pitcherK: "Líderes en ponches",
        ip: "Líderes en entradas",
        era: "Líderes en ERA",
        whip: "Líderes en WHIP",
        k9: "Líderes en K/9",
        bb9: "BB/9 (descontrol)",
        pitcherRecentForm: "Forma reciente (lanzadores)",
        pitcherHotStreaks: "Rachas calientes (lanzadores)",
        pitcherColdStreaks: "Rachas frías (lanzadores)",
        pitcherConsistency: "Lanzadores más consistentes",
        pitcherBestGames: "Mejores actuaciones (lanzadores)",
        pitcherRisers: "Suben (lanzadores)",
        pitcherFallers: "Bajan (lanzadores)",
        hr: "Líderes en HR",
        rbi: "Líderes en RBI",
        sb: "Líderes en SB",
        avg: "Líderes en AVG",
        hits: "Líderes en hits",
        runs: "Líderes en carreras",
        bb: "Líderes en BB",
        tb: "Líderes en bases totales",
        xbh: "Líderes en XBH",
        so: "Más ponches",
        obp: "Líderes en OBP",
        slg: "Líderes en SLG",
        ops: "Líderes en OPS",
        kRate: "Tasa de K más alta",
        bbRate: "Tasa de BB más alta",
        recentForm: "Forma reciente",
        hotStreaks: "Rachas calientes",
        coldStreaks: "Rachas frías",
        consistency: "Más consistentes",
        bestGames: "Mejores actuaciones (juego)",
        risers: "Suben esta semana",
        fallers: "Bajan esta semana",
        teamScoring: "Total de puntos por equipo",
        teamTopPerformers: "Mejores rendimientos por equipo",
      },
      teamFilter: {
        all: "Todos los equipos",
        label: "Equipo",
      },
      columns: {
        rank: "#",
        player: "Jugador",
        team: "Equipo",
        games: "GP",
        points: "PTS",
        value: "Valor",
        opp: "Riv",
        date: "Fecha",
        line: "Línea",
        through: "Hasta",
        streak: "Racha",
        mean: "Media",
        stdDev: "σ",
        priorWeek: "Anterior",
        currentWeek: "Actual",
        delta: "Δ",
        players: "Jugadores",
        fpPerGame: "FP/G",
        totalFp: "FP total",
        ip: "IP",
        era: "ERA",
        whip: "WHIP",
        k9: "K/9",
        bb9: "BB/9",
        wins: "G",
        k: "K",
        er: "CL",
      },
      eyebrow: {
        minGames: "Mín. {count} partidos",
        minAb: "Mín. {count} AB",
        minIp: "Mín. {count} IP",
        week: "Semana {week}",
        lastN: "Últimos {count} partidos",
        weekDelta: "Δ vs semana anterior",
      },
      empty: "Aún no hay suficientes turnos al bate.",
    },
    scoring: {
      title: "Sistema de puntos",
      subtitle:
        "Cómo se otorgan los puntos fantasy por cada evento de bateo. Solo lectura — para compartir con los participantes.",
      version: "Reglas v{version}",
      positive: "Puntuación positiva",
      penalties: "Penalizaciones",
      columns: {
        rule: "Evento",
        abbr: "Abrev.",
        points: "Puntos",
      },
      rules: {
        single: "Sencillo",
        double: "Doble",
        triple: "Triple",
        homeRun: "Jonrón",
        rbi: "Carrera impulsada",
        run: "Carrera anotada",
        walk: "Base por bolas",
        stolenBase: "Base robada",
        caughtStealing: "Atrapado robando",
        strikeout: "Ponche",
      },
      pitcherTitle: "Sistema de puntos para lanzadores",
      pitcherSubtitle:
        "Cómo se otorgan los puntos por cada evento de lanzamiento. Cualquier jugador en roster con estadísticas de pitcheo suma estos puntos.",
      pitcherVersion: "Reglamento de pitcheo v{version}",
      pitcherNote:
        "Las entradas se guardan como outs (3 outs = 1 IP); se muestran como .0/.1/.2 según convención KNBSB. Las decisiones (W/L) se leen del box score.",
      pitcherRules: {
        perOut: "Out registrado (1 IP = 3 outs)",
        perStrikeout: "Ponche",
        perWin: "Victoria",
        perSave: "Salvada",
        perCompleteGame: "Juego completo",
        perShutout: "Blanqueada",
        perEarnedRun: "Carrera limpia permitida",
        perWalk: "Base por bolas otorgada",
        perHitAllowed: "Hit permitido",
        perLoss: "Derrota",
      },
      note: "Los puntos totales de un jugador son la suma de cada evento en todos los partidos. Las reglas se fijan por liga al crearla y no cambian durante la temporada.",
      maintenance: "Mantenimiento",
      saving: "Guardando…",
      saved: "Guardado. Ejecuta «Actualizar todas las estadísticas» para aplicarlo a las estadísticas existentes.",
      savedHint:
        "Ejecuta «Actualizar todas las estadísticas» para aplicarlo a las estadísticas existentes.",
      rescoreButton: "Actualizar todas las estadísticas",
      rescoreRunning: "Recalculando…",
      rescoreConfirm:
        "¿Volver a aplicar las reglas actuales de bateo y pitcheo a cada fila, recalcular los totales de temporada y refrescar la puntuación histórica derivada?",
      rescoreDone: "Se recalcularon {count} filas de bateo y pitcheo.",
      rescoreHint:
        "Ejecuta esto tras editar los puntos para que partidos, clasificaciones, totales semanales, bonos de pitcheo y perfiles históricos usen las mismas reglas.",
      dangerZone: "Zona de peligro",
      purgeTitle: "Purgar todas las estadísticas",
      purgeSubtitle:
        "Borra todas las líneas de box score introducidas y pone a cero los totales de cada mánager. Drafts, ligas, jugadores y partidos se mantienen. Útil para empezar la temporada de cero.",
      purgeButton: "Purgar todas las estadísticas",
      purgeRunning: "Purgando…",
      purgeConfirm1:
        "Esto borrará TODAS las líneas de box score introducidas y pondrá a cero los totales de cada mánager. ¿Continuar?",
      purgeConfirm2: "Última oportunidad — no se puede deshacer. ¿Purgar todo?",
      purgeDone: "Borradas {stats} filas · reiniciados {totals} totales de mánagers.",
    },
    cutPicks: {
      title: "Quitar las últimas X picks por mánager",
      subtitle:
        "Elimina las picks más recientes (mayor número de pick) de cada mánager en una liga. Útil si el draft hizo demasiadas rondas y quieres reducir las plantillas antes de empezar la temporada.",
      leagueLabel: "Liga",
      leaguePlaceholder: "Elige una liga…",
      rosterTag: "{members} mgrs · plantilla {size}",
      countLabel: "Cantidad",
      adjustLabel: "Reducir plantilla en consecuencia",
      button: "Quitar las últimas {count}",
      running: "Quitando…",
      confirm1: "¿Quitar las últimas {count} pick(s) de cada mánager en “{league}”?",
      confirm2: "Última oportunidad — no se puede deshacer. ¿Continuar?",
      done: "{picks} picks quitadas en {members} mánagers · plantilla ahora {roster}.",
    },
  },
};

const nl: Dictionary = {
  app: {
    name: "KNBSB Fantasy",
    tagline: "Hoofdklasse · Fantasy Honkbal",
  },
  nav: {
    home: "Start",
    league: "Competitie",
    settings: "Instellingen",
    more: "Meer",
    moreEmpty: "Open een competitie om de secties hier te zien.",
    primary: "Primair",
    leagueMenu: "Competitiemenu",
    commandDeck: "Bedieningspaneel",
    joinLeagueToUnlock: "Doe mee aan een competitie om competitienavigatie te ontgrendelen.",
    menuItems: "{count} menu-items",
    signOut: "Uitloggen",
    language: "Taal",
  },
  locale: {
    en: "English",
    es: "Español",
    nl: "Nederlands",
  },
  auth: {
    signInTitle: "Welkom terug",
    signInSubtitle: "Log in om je competitie te beheren.",
    signUpTitle: "Maak je account aan",
    signUpSubtitle: "Het duurt ongeveer een minuut.",
    email: "E-mailadres",
    password: "Wachtwoord",
    confirmPassword: "Bevestig wachtwoord",
    showPassword: "Wachtwoord tonen",
    hidePassword: "Wachtwoord verbergen",
    name: "Volledige naam",
    signIn: "Inloggen",
    signUp: "Account aanmaken",
    newHere: "Nieuw hier?",
    alreadyHaveOne: "Heb je al een account?",
    createOne: "Maak er een aan",
    signInLink: "Inloggen",
    errorGeneric: "Er ging iets mis. Probeer het opnieuw.",
  },
  home: {
    welcome: "Welkom, {name}",
    homeBase: "Jouw fantasy honkbal-thuisbasis.",
    getStarted: "Aan de slag",
    noLeagueYet: "Nog geen competitie",
    noLeagueYetBody: "Maak een competitie aan of doe mee met een uitnodigingscode.",
    onboardTitle: "Eén competitie, één seizoen.",
    onboardLead:
      "Start een privé-competitie voor je vrienden, of stap er een binnen met een uitnodigingscode. De Hoofdklasse, jouw manier.",
    createLeague: "Competitie starten",
    createLeagueDesc: "Jij bent de commissaris. Bepaal de regels en nodig de rest uit.",
    joinLeague: "Meedoen met code",
    joinLeagueDesc: "Heb je een 8-tekens uitnodiging? Plak hem in en kies je teamnaam.",
    or: "of",
  },
  league: {
    overview: "Competitie-overzicht",
    season: "Seizoen {year}",
    managers: "Teammanagers",
    managersOf: "{count} van {max}",
    rosterSize: "Selecties van {count} spelers",
    inviteCode: "Uitnodigingscode",
    inviteCodeHelp: "Deel deze met je vrienden. Iedereen met de code kan meedoen.",
    copy: "Kopieer",
    copied: "Gekopieerd",
    regenerate: "Vernieuwen",
    state: {
      setup: "Voorbereiding",
      drafting: "Draft",
      active: "Actief",
      complete: "Afgerond",
    },
    commissioner: "Commissaris",
    startDraft: "Draft starten",
    needTwo: "Minimaal twee managers vereist",
    yourTeam: "Jouw team",
    activity: {
      eyebrow: "Live competitiefeed",
      title: "Rosteractiviteit",
      open: "Rosteractiviteit openen",
      close: "Rosteractiviteit sluiten",
      loading: "Rosteractiviteit laden...",
      empty: "Nog geen rosterwijzigingen.",
      clearedEmpty: "Je bent bij. Toon geschiedenis om oudere wijzigingen te bekijken.",
      clear: "Wissen",
      clearing: "Wissen...",
      showHistory: "Toon geschiedenis",
      showLatest: "Toon nieuwste",
      rowTitle: "{team} pakte {player} op",
      rowMeta: "Dropte {dropped}",
      tradeRowTitle: "{team} ruilde voor {player}",
      tradeRowMeta: "Stuurde {sent}",
    },
    trades: {
      title: "Ruilen",
      subtitle: "Bied een een-op-een ruil aan en laat de andere manager accepteren of weigeren.",
      newTrade: "Nieuwe trade",
      incoming: "Binnenkomend",
      outgoing: "Verzonden",
      history: "Geschiedenis",
      emptyIncoming: "Geen binnenkomende aanbiedingen.",
      emptyOutgoing: "Geen verzonden aanbiedingen.",
      emptyHistory: "Nog geen afgeronde trades.",
      offerLine: "{from} biedt {offered} voor {requested}",
      expires: "Vervalt {date}",
      resolved: "Afgerond {date}",
      accept: "Accepteren",
      decline: "Weigeren",
      cancel: "Annuleren",
      accepting: "Accepteren...",
      declining: "Weigeren...",
      cancelling: "Annuleren...",
      proposeTitle: "Trade voorstellen",
      proposeSubtitle: "Kies wie je wilt en welke speler je terug aanbiedt.",
      chooseManager: "Manager kiezen",
      requestedPlayer: "Gevraagde speler",
      offeredPlayer: "Jouw aanbod",
      chooseRequested: "Kies een speler om te vragen",
      chooseOffered: "Kies een speler van jou",
      summary: "Controle",
      youReceive: "Jij krijgt",
      youSend: "Jij geeft",
      back: "Terug",
      confirm: "Ruil bevestigen",
      submit: "Aanbod sturen",
      sending: "Versturen...",
      unavailable: "Trades zijn beschikbaar na de draft zolang de competitie actief is.",
      openLimit: "{count} open aanbiedingen toegestaan",
      proposeFromRoster: "Trade aanbieden",
      statuses: {
        pending: "Open",
        accepted: "Geaccepteerd",
        declined: "Geweigerd",
        cancelled: "Geannuleerd",
        expired: "Vervallen",
        superseded: "Gesloten",
      },
      created: "Trade-aanbod verstuurd.",
      accepted: "Trade geaccepteerd.",
      declined: "Trade geweigerd.",
      cancelled: "Trade geannuleerd.",
      failed: "Trade-actie mislukt.",
    },
    activeRoster: {
      eyebrow: "Actieve selectie",
      title: "Actieve selectiegrootte",
      description:
        "Hoeveel gedrafte spelers per manager meetellen voor de stand. Selectie: {roster}.",
      fieldLabel: "Aantal actief",
      allCount: "Alle gedrafte spelers tellen mee voor het seizoenstotaal.",
      partialCount: "Top {active} scoren · {bench} op de bank.",
      save: "Opslaan",
      saving: "Opslaan…",
      invalid: "Aantal actief moet tussen 1 en {roster} liggen.",
    },
    manage: {
      title: "Competitie-instellingen",
      description:
        "Wijzig competitiegegevens of verwijder de competitie volledig. Vergrendeld zodra de draft begint.",
      detailsTitle: "Gegevens",
      maxManagersHint: "Al lid: {count}.",
      edit: "Bewerken",
      save: "Wijzigingen opslaan",
      cancel: "Annuleren",
      remove: "Verwijderen",
      removeMember: "Verwijder {team} uit de competitie",
      scoringStart: "Scorestart",
      scoringStartAll: "Hele seizoen",
      scoringStartTitle: "Scoreperiode",
      scoringStartDescription:
        "Wedstrijden voor deze datum tellen niet mee voor deze competitie, maar blijven beschikbaar voor andere competities.",
      clearScoringStart: "Wissen",
      draftTitle: "Draftbeheer",
      reset: "Draft resetten",
      resetConfirm: "Ja, resetten",
      resetDescription:
        "Annuleert de huidige draft en zet de competitie terug op opzet. Alle picks vervallen.",
      dangerZone: "Gevarenzone",
      deleteDescription:
        "Verwijdert de competitie, alle managers en eventuele draftpicks definitief.",
      delete: "Competitie verwijderen",
      deleteConfirm: "Ja, verwijderen",
      pitcherDraft: {
        title: "Werpersdraft",
        description:
          "Voeg werpersplekken toe aan deze competitie en houd een snake draft alleen voor werpers. Ronde 1 wordt omgekeerd ten opzichte van de hoofddraft (slechtste slagteam kiest eerst).",
        slotsLabel: "Werpersplekken",
        slotsHint: "1–10. De totale selectie groeit met dit aantal.",
        setSlots: "Plekken opslaan",
        start: "Werpersdraft starten",
        setFirst: "Stel eerst werpersplekken in.",
        slotsSaved: "Werpersplekken ingesteld op {count}.",
        invalidSlots: "Werpersplekken moeten een geheel getal van 1 tot 10 zijn.",
      },
      endLeague: {
        title: "Competitie afsluiten",
        description:
          "De stand wordt definitief en de competitie wordt bevroren. Pickups en statistieken-totalen wijzigen niet meer voor deze competitie. Dit kan niet ongedaan gemaakt worden.",
        confirm: "Competitie afsluiten",
        confirmHint: "Typ de naam van de competitie om te bevestigen.",
        success: "Competitie afgesloten.",
      },
      ended: "Competitie afgesloten op {date}",
      seasonEnd: {
        title: "Einde seizoen",
        description:
          "Zodra deze datum verstreken is en elke geplande wedstrijd op of vóór die dag definitief is, sluit de competitie automatisch af.",
        fieldLabel: "Einddatum seizoen",
        clear: "Wissen",
      },
    },
    games: {
      title: "Wedstrijden",
      subtitle: "Wedstrijden",
      empty: "Nog geen wedstrijden dit seizoen.",
      emptyTitle: "Stille kalender",
      comingSection: "Komende wedstrijden",
      previousSection: "Eerdere wedstrijden",
      columnDate: "Datum",
      columnHome: "Thuis",
      columnAway: "Uit",
      detail: {
        eyebrow: "Boxscore",
        backToList: "Alle wedstrijden",
        yourPlayer: "Jouw speler",
        draftedBy: "Gedraft door {team}",
        youScored: "Je scoorde {points} punten in deze wedstrijd",
        nonePlayed: "Geen van jouw spelers kwam in deze wedstrijd uit.",
        noStatsYet: "Nog geen statistieken voor deze wedstrijd.",
        showOnlyMine: "Alleen mijn spelers",
        showAll: "Alles",
      },
      headers: {
        AB: "AB",
        H: "H",
        D2B: "2B",
        D3B: "3B",
        HR: "HR",
        RBI: "RBI",
        R: "R",
        BB: "BB",
        SB: "SB",
        CS: "CS",
        SO: "SO",
        PTS: "PTS",
        IP: "IP",
        ER: "ER",
        DEC: "DEC",
      },
    },
    standings: {
      title: "Stand",
      subtitle: "Waar iedereen staat, live bijgewerkt zodra box scores binnenkomen.",
      empty: "Nog geen punten gescoord — wacht op de eerste box score.",
      emptyTitle: "Lege ranglijst",
      regular: "Regulier seizoen",
      champion: "Kampioen",
      columnRank: "#",
      columnTeam: "Ploeg",
      columnPoints: "PTS",
      columnGames: "W",
      youLabel: "Jij",
      weeklyChart: {
        title: "Punten per week",
        subtitle: "Seizoenspunten per week.",
        empty: "Nog niet genoeg wekelijkse gegevens om te tonen.",
      },
      topPlayers: {
        title: "Topscorers",
        subtitle: "Hoogst scorende spelers in de hele competitie.",
        seeAll: "Alle spelers bekijken",
        empty: "Nog geen spelersstatistieken.",
      },
    },
    scoring: {
      title: "Puntentelling",
      subtitle:
        "Hoe fantasy-punten worden toegekend. Alleen-lezen — ingesteld door de commissaris.",
      hitterTitle: "Slaan",
      pitcherTitle: "Werpen",
      columns: {
        rule: "Gebeurtenis",
        abbr: "Afk",
        points: "Punten",
      },
    },
    players: {
      title: "Spelers",
      subtitle: "Alle gedrafte spelers gerangschikt op fantasy-punten dit seizoen.",
      empty: "Nog geen statistieken van gedrafte spelers.",
      emptyTitle: "Lege ranglijst",
      columnRank: "#",
      columnPlayer: "Speler",
      columnTeam: "KNBSB-team",
      columnDrafter: "Gedraft door",
      columnGames: "W",
      columnPoints: "PTS",
      freeAgents: {
        title: "Vrije spelers",
        subtitle:
          "Niet-gedrafte spelers gesorteerd op punten. Pak er één om hem in je selectie te ruilen.",
        empty: "Geen niet-gedrafte spelers in de pool.",
        searchPlaceholder: "Zoek speler of team",
        noMatch: "Geen speler komt overeen met de zoekopdracht.",
        pickUp: "Oppakken",
        picking: "Oppakken…",
        columnMove: "Wissel",
        pickUpPlayer: "Speler oppakken",
        pickUpName: "{name} oppakken",
        replaceHint:
          "Kies welke selectieslot je vervangt. De nieuwe speler neemt die pickplek over en seizoenstotalen worden direct herberekend.",
        noCompatibleSlots: "Er zijn geen passende selectieslots beschikbaar.",
        dismissError: "Fout sluiten",
      },
      allPlayers: {
        title: "Alle gedrafte spelers",
        subtitle:
          "Elke speler die nu in deze competitie op een selectie staat, gesorteerd op punten.",
        empty: "Er is nog geen speler gedraft.",
        columnTeam: "Ploeg",
      },
      profile: {
        title: "Spelersprofiel",
        notFound: "Speler niet gevonden",
        noLongerAvailable: "Deze speler is niet meer beschikbaar in de competitiegegevens.",
        backToPlayers: "Spelers",
        unknownPlayer: "Onbekende speler",
        team: "Ploeg",
        fantasy: "Fantasyteam",
        freeAgent: "Vrije speler",
        undrafted: "Niet gedraft",
        unassigned: "Niet toegewezen",
        noCompetition: "Geen competitie",
        pickMeta: "Keuze {pick}",
        gameLogs: "Wedstrijdlogs",
        scoringRun: "Puntenreeks",
        noGameLogs: "Er zijn nog geen wedstrijdlogs voor deze speler.",
        hitting: "Slaan",
        pitching: "Werpen",
        viewGameAria: "Open wedstrijddetails voor {opponent} op {date}",
      },
      dropPickup: {
        title: "Vervang er één door {name}",
        titleFallback: "Vervang één van je spelers",
        subtitle:
          "Kies welke speler je laat gaan. De seizoenspunten van de nieuwe speler tellen direct voor jou.",
        subtitlePitcher:
          "Kies welke pitcher je laat gaan. De seizoenspunten van de nieuwe speler tellen direct voor jou.",
        subtitleHitter:
          "Kies welke veldspeler je laat gaan. De seizoenspunten van de nieuwe speler tellen direct voor jou.",
        pickN: "Keuze {n}",
        noRoster: "Je hebt geen gedrafte spelers om te vervangen.",
        noPitchers: "Je hebt geen pitchers om te vervangen.",
        noHitters: "Je hebt geen veldspelers om te vervangen.",
        cancel: "Annuleren",
      },
    },
    profile: {
      backToStandings: "Alle standen",
      eyebrow: "Teammanager",
      stats: {
        regularPoints: "Totaal",
        regularGames: "Reguliere G",
      },
      roster: {
        title: "Selectie",
        subtitle: "Gesorteerd op fantasy-punten dit seizoen.",
        empty: "Geen gedrafte spelers.",
        columnPick: "Keuze",
        columnPlayer: "Speler",
        columnTeam: "Ploeg",
        columnGames: "W",
        columnRegular: "Seiz.",
        columnTotal: "Totaal",
        autoBadge: "Automatisch",
      },
      recent: {
        title: "Bijdragehistorie",
        subtitle: "Laatste scorende selectieregels. Open de volledige weergave van oud naar nieuw.",
        empty: "Nog geen wedstrijdbijdragen.",
        columnDate: "Datum",
        columnPlayer: "Speler",
        columnGame: "Wedstrijd",
        columnPoints: "PTS",
        backToManager: "Managerprofiel",
        historyTitle: "Bijdragen",
      },
    },
    dashboard: {
      lastGame: "Laatste wedstrijd",
      noLastGame: "Nog geen wedstrijden gespeeld.",
      top3: "Top drie",
      rank: "Rang",
      points: "Punten",
      pointsAbbr: "pnt",
      finalStandingsTitle: "Eindstand",
      finalStandingsEndedOn: "Seizoen afgesloten {date}",
      seeStandings: "Volledige stand bekijken",
      yourRoster: "Jouw selectie",
      yourRosterSubtitle: "Vorm over de laatste drie wedstrijden van elke speler.",
      rosterEmpty: "De draft is nog niet afgerond.",
      columnLast3: "Laatste 3",
      columnForm: "Vorm",
      columnL3: "L3",
      columnLastThreePoints: "Laatste 3",
      columnSeason: "Seizoen",
      columnTotal: "Totaal",
      lastThreeGames: "Laatste drie wedstrijden",
      formNoGame: "—",
      yourEdge: "Jouw voorsprong",
      atTheTop: "Bovenaan de stand",
      gapToLeader: "{points} van koploper",
      aboveCutline: "{points} boven de streep",
      belowCutline: "{points} onder de streep",
      cutlineFirm: "Hangt boven de streep met {points}",
      hottest: "In vorm · {name}",
      coldest: "Uit vorm · {name}",
      noForm: "Nog geen recente vorm.",
      benchDivider: "Bank · {count} spelers tellen niet mee",
      contributions: {
        title: "Recente bijdragen",
        subtitle: "De laatste wedstrijdregels van je selectie — nieuwste eerst.",
        sentence: "{player} droeg bij aan {team} met {points}",
        statLine: "{kind}-regel",
        empty: "Nog geen wedstrijdbijdragen.",
        seeAll: "Bekijk alles",
        pageTitle: "Recente bijdragen",
        pageSubtitle: "Elke meetellende wedstrijd waarin je selectie scoorde, nieuwste eerst.",
        columnDate: "Datum",
        columnPlayer: "Speler",
        columnTeam: "Ploeg",
        columnOpponent: "Tegen",
        columnPoints: "PTS",
      },
      gameLog: {
        openAria: "Open wedstrijdverloop voor {name}",
        subtitle:
          "Elke meetellende wedstrijd waarin deze speler punten scoorde. Nieuwste bovenaan.",
        empty: "Nog geen punten gescoord.",
        hittingSection: "Slaan",
        pitchingSection: "Pitchen",
        totals: "Totaal · {count} W",
        columnDate: "Datum",
        columnOpponent: "Tegen",
        colAB: "AB",
        colR: "R",
        colH: "H",
        col2B: "2H",
        col3B: "3H",
        colHR: "HR",
        colRBI: "RBI",
        colBB: "BB",
        colSB: "SB",
        colK: "K",
        colFP: "FP",
        colIP: "IP",
        colER: "EP",
        colHA: "H",
        colBBA: "BB",
        colKA: "K",
        colDec: "Bes",
      },
    },
  },
  create: {
    title: "Maak je competitie",
    subtitle:
      "Bepaal de regels. Zodra de draft start, staan de instellingen vast voor het seizoen.",
    leagueName: "Naam competitie",
    teamName: "Jouw teamnaam",
    season: "Seizoen",
    maxManagers: "Max. managers",
    rosterSize: "Selectiegrootte",
    hitterSlots: "Slagmensen per team",
    pitcherSlots: "Werpers per team",
    totalRoster: "Totale selectiegrootte: {total}",
    pickTimer: "Pickklok (seconden)",
    draftTimerEnabled: "Pickklok inschakelen",
    draftTimerEnabledHelp:
      "De commissaris bepaalt het tempo. Managers picken op hun eigen snelheid.",
    pickClock: "Pickklok",
    pickClockTimed: "Met klok",
    pickClockUntimed: "Zonder klok",
    scoringStart: "Scorestart",
    scoringStartHelp:
      "Optioneel. Wedstrijden vóór deze datum tellen niet voor deze competitie. Laat leeg om alle geschikte wedstrijden mee te tellen.",
    capacityOk: "Capaciteit OK — {picks} picks van {pool} geschikte spelers ({buffer}% marge)",
    capacityFail: "Capaciteitscontrole mislukt.",
    submit: "Competitie aanmaken",
    submitFail: "Kon competitie niet aanmaken.",
  },
  join: {
    title: "Doe mee aan een competitie",
    subtitle: "Heb je een uitnodigingscode van de commissaris? Plak hem hieronder.",
    inviteCode: "Uitnodigingscode",
    teamName: "Jouw teamnaam",
    submit: "Meedoen",
    submitFail: "Kon niet meedoen aan de competitie.",
  },
  settings: {
    title: "Instellingen",
    subtitle: "Account, taal en instellingen per competitie.",
    signedInAs: "Ingelogd als",
    signOut: "Uitloggen",
    language: "Taal",
    languageHelp: "Kies hoe de app je aanspreekt.",
    tabs: {
      account: "Account",
      leagues: "Competities",
      language: "Taal",
    },
    leagues: {
      empty: {
        title: "Nog geen competities",
        body: "Maak of doe mee aan een competitie via Start om hier te beheren.",
      },
    },
  },
  pwa: {
    install: {
      title: "Installeer Fantasy",
      body: "Zet hem op je beginscherm voor één tik toegang en een native gevoel.",
      iosBody: "Tik op het deelicoon in Safari en kies 'Zet op beginscherm'.",
      cta: "Installeren",
      dismiss: "Sluiten",
    },
    offline: {
      banner: "Je bent offline - laatste data getoond",
    },
    update: {
      title: "Update beschikbaar",
      body: "Er is een nieuwe versie van Fantasy. Laad opnieuw om bij te werken.",
      cta: "Opnieuw laden",
      dismiss: "Straks",
    },
  },
  share: {
    button: "Delen",
    failed: "Kon de deelkaart niet genereren.",
  },
  validation: {
    required: "Verplicht",
  },
  errors: {
    routeEyebrow: "Routefout",
    routeTitle: "Dit scherm kon niet worden geladen",
    generic: "Er ging iets mis.",
    tryAgain: "Opnieuw proberen",
  },
  ui: {
    dataTable: {
      noRows: "Geen rijen.",
      pageSize: "Paginagrootte",
      page: "Pagina",
      of: "van",
      prev: "Vorige",
      next: "Volgende",
      search: "Zoeken",
      clear: "Wissen",
      rangeFrom: "{label} vanaf",
      rangeTo: "{label} tot",
    },
    datePicker: {
      selectDate: "Kies een datum",
      selectDateTime: "Kies datum en tijd",
      pickRange: "Kies een periode",
      previousMonth: "Vorige maand",
      nextMonth: "Volgende maand",
      time: "Tijd",
      hour: "Uur",
      minute: "Minuut",
      clear: "Wissen",
    },
    nightSearch: {
      placeholder: "Zoek spelers...",
      filterPlayers: "Spelers filteren",
    },
  },
  draft: {
    title: "Draft",
    onClock: {
      eyebrow: "Aan de beurt",
      yourTurn: "Jouw pick",
      waitingFor: "Wachten op {team}",
      pickNumber: "Pick {pick} · Ronde {round}",
      pitcherPill: "ALLEEN WERPERS",
    },
    timer: {
      secondsLeft: "{seconds}s",
      paused: "Gepauzeerd",
      pausedOff: "Gepauzeerd · timer uit",
      expired: "Automatisch kiezen…",
      off: "Timer uit",
    },
    autoPick: {
      toggleLabel: "Mijn favorieten automatisch kiezen (15s)",
      eyebrow: "Autopick · 15s",
      body: "Uit mijn favorieten, op volgorde",
      tapToEnable: "Tik om in te schakelen",
      needFavorites: "Markeer minstens één speler als favoriet om dit in te schakelen.",
      firingFromFavorites: "Automatisch kiezen uit favorieten…",
      explainer: {
        title: "Zo werkt autopick",
        body1:
          "Als de competitietimer uit staat en jij aan de beurt bent, heb je 15 seconden voordat autopick start.",
        body2:
          "Alleen spelers uit je favorieten worden gekozen, op volgorde. Als al je favorieten al gedraft zijn, wordt er geen pick gedaan en blijf je aan de beurt.",
        body3: "Je kunt dit op elk moment uitschakelen.",
        confirm: "Begrepen — inschakelen",
        cancel: "Annuleren",
      },
    },
    available: {
      title: "Beschikbare spelers",
      searchPlaceholder: "Spelers zoeken",
      teamFilterAll: "Alle teams",
      favoritesChip: "Favorieten ({count})",
      empty: "Geen spelers gevonden.",
      emptyFavorites:
        "Geen favorieten gevonden — markeer een speler met een ster om hem toe te voegen.",
      pickButton: "Kiezen",
      notYourTurn: "Niet jouw beurt",
    },
    myPicks: {
      title: "Mijn picks",
      countLabel: "{count} keuzes",
      openButton: "Mijn picks ({count})",
      openLabel: "Mijn picks tonen",
      empty: "Je hebt nog geen spelers gedraft.",
      notAMember: "Je bent geen lid van deze competitie.",
    },
    nextPicks: {
      label: "Hierna",
    },
    turnAlert: {
      title: "Jij bent aan de beurt",
      body: "Je bent aan de beurt — ga naar de Draft-tab om je pick te doen.",
      goToDraft: "Naar Draft",
      dismiss: "Sluiten",
    },
    history: {
      title: "Pickgeschiedenis",
      empty: "Nog geen picks.",
      roundLabel: "Ronde {round}",
      autoBadge: "Automatisch",
      pickByManager: "{team} → {player}",
    },
    board: {
      title: "Draftbord",
      titlePitcher: "Pitcher-draftbord",
      onClockPill: "Aan zet",
      notStarted: "Draft is nog niet gestart.",
    },
    tabs: {
      draft: "Draft",
      board: "Draftbord",
    },
    queue: {
      title: "Mijn favorieten",
      empty:
        "Markeer spelers met een ster om ze als favoriet toe te voegen. De bovenste wordt automatisch gekozen als je tijd verloopt.",
      addToQueue: "Toevoegen aan favorieten",
      removeFromQueue: "Verwijderen uit favorieten",
      clearAll: "Wissen",
      topUp: "Volgende",
      pickNow: "Nu kiezen",
      muteAlerts: "Geluid uit",
      unmuteAlerts: "Geluid aan",
      searchPlaceholder: "Favorieten filteren",
      noMatch: "Geen favoriet komt overeen.",
      moveUp: "Omhoog",
      moveDown: "Omlaag",
    },
    roster: {
      title: "Mijn selectie",
      subtitle: "Spelers gedraft voor {team} dit seizoen.",
      empty: "Je hebt geen spelers gedraft.",
    },
    controls: {
      start: "Draft starten",
      startPitcher: "Werpersdraft starten",
      startPitcherConfirm:
        "Aanvullende werpersdraft starten? De volgorde van ronde 1 is omgekeerd ten opzichte van de hoofddraft.",
      pause: "Pauzeren",
      resume: "Hervatten",
      startConfirm: "Draft nu starten?",
      timerOn: "Timer aan",
      timerOff: "Timer uit",
      cancel: "Annuleren",
      disableTimerTitle: "Pickklok uitzetten?",
      disableTimerBody:
        "De competitietimer stopt. Leden met persoonlijke autopick houden een klok van 15s voor favorieten; alle anderen blijven aan de beurt tot ze handmatig kiezen of jij contact met ze opneemt.",
      disableTimerConfirm: "Ja, uitzetten",
      enableTimerTitle: "Pickklok herstellen?",
      enableTimerBody:
        "De competitietimer telt opnieuw af vanaf de volledige pickduur. Persoonlijke autopickklokken stoppen — autopicks gebruiken de standaard willekeurige fallback van de competitie wanneer iemands favorieten op zijn.",
      enableTimerConfirm: "Ja, herstellen",
    },
    errors: {
      notYourTurn: "Het is niet jouw beurt.",
      alreadyDrafted: "Die speler is al gepickt.",
      draftNotActive: "De draft is niet actief.",
      slotFull: "Je plekken voor dat type speler zijn vol.",
      notAMember: "Je bent geen lid meer van deze competitie.",
      rateLimited: "Even rustig — probeer het zo opnieuw.",
    },
    status: {
      complete: "Draft afgerond",
      completeEyebrow: "Klaar",
    },
    filters: {
      kind: "Soort",
      all: "Alles",
      pitchers: "Werpers",
      hitters: "Veldspelers",
    },
  },
  player: {
    kind: {
      short: {
        pitcher: "P",
        positionPlayer: "H",
      },
      long: {
        pitcher: "Werper",
        positionPlayer: "Veldspeler",
      },
    },
    slot: {
      hitter: "H",
      pitcher: "P",
    },
  },
  roster: {
    slotLabel: "{kind}{index}",
  },
  freeAgents: {
    filters: {
      kind: "Soort",
      all: "Alles",
      pitchers: "Werpers",
      hitters: "Veldspelers",
    },
  },
  admin: {
    printRoster: "Selecties printen (PDF)",
    printRosterTitle: "ONPOINT — Selecties",
    printRosterGeneratedAt: "Gegenereerd op {date}",
    shell: {
      section: "Administraci?n",
      operatorConsole: "Operatorconsole",
      adminConsole: "Beheerconsole",
      navigation: "Beheernavigatie",
      sections: "Beheeronderdelen",
      breadcrumb: "Kruimelpad",
      closeMenu: "Menu sluiten",
      openMenu: "Menu openen",
    },
    auth: {
      title: "Beheerlogin",
      subtitle: "Alleen voor commissarissen en operators.",
      signInError: "Inloggen is mislukt.",
    },
    teams: {
      title: "Ploegen",
      subtitle:
        "Clubs uit de selectiepool. Klik op een rij voor de slaglijst en statistieken per speler.",
      searchPlaceholder: "Zoek teams…",
      detailSubtitle:
        "{count} slagmensen · totalen samengevoegd uit elke geldige wedstrijd met ingevoerde stats.",
      loading: "Laden...",
      columns: {
        name: "Ploeg",
        shortName: "Afk.",
        hitters: "Slagers",
        games: "Wedstrijden",
        points: "Punten",
      },
    },
    leagues: {
      title: "Competities",
      subtitle:
        "Draftvolgorde, pickhistorie, selectiewijzigingen en standen voor elke aangemaakte competitie.",
      loading: "Laden...",
      notFound: "Niet gevonden",
      notFoundTitle: "Competitie niet gevonden",
      notFoundDescription: "Deze competitie is mogelijk verwijderd of de URL is verouderd.",
      detailSubtitle: "{season} / {members} teamleiders / {hitters} S / {pitchers} W",
      emptyTitle: "Nog geen competities",
      emptyDescription:
        "Aangemaakte competities verschijnen hier zodra managers meedoen en drafts beginnen.",
      metrics: {
        leagues: "Competities",
        drafts: "Draftmomenten",
        managers: "Teamleiders",
        picks: "Keuzes",
      },
      searchLabel: "Competities zoeken",
      searchPlaceholder: "Competitie, manager, seizoen of status",
      noMatchTitle: "Geen passende competities",
      noMatchDescription: "Wis de zoekterm om terug te gaan naar de volledige beheerweergave.",
      columns: {
        league: "Competitie",
        status: "Stand",
        managers: "Teamleiders",
        drafts: "Draftrondes",
        picks: "Keuzes",
        leader: "Leider",
        lastPick: "Laatste keuze",
        open: "Openen",
      },
      pointsAbbr: "pnt",
      noLeader: "Geen leider",
      noPicks: "Geen keuzes",
      open: "Openen",
      openLeague: "{league} openen",
      states: {
        setup: "Instellen",
        drafting: "Aan het draften",
        active: "Actief",
        complete: "Afgerond",
      },
    },
    leagueDetail: {
      metrics: {
        managers: "Teamleiders",
        drafts: "Draftrondes",
        picks: "Keuzes",
        leader: "Leider",
        noLeader: "Geen leider",
      },
      emptyDraft: {
        title: "Nog geen draft aangemaakt",
        description: "Het draftbord verschijnt hier zodra deze competitie begint met draften.",
      },
      draft: {
        boardTitle: "Draftbord",
        pickProgress: "{picked}/{total} keuzes",
        completedAt: "afgerond op {date}",
        pickHistory: "Pickgeschiedenis",
        noOrder: "Geen draftvolgorde toegewezen.",
        order: "Volgorde {order}",
        onClock: "Aan zet",
        auto: "Automatisch",
        teamUnknown: "Ploeg onbekend",
        noPicks: "Nog geen spelers gedraft.",
        roundAbbr: "Ronde {round}",
        phases: {
          main: "Hoofddraft",
          pitcherSupplemental: "Werpersdraft",
        },
        states: {
          pending: "In afwachting",
          active: "Actief",
          paused: "Gepauzeerd",
          complete: "Afgerond",
        },
        columns: {
          pick: "Keuze",
          manager: "Teamleider",
          draftee: "Gedrafte speler",
          slot: "Plek",
          points: "Punten",
        },
      },
      standings: {
        title: "Stand",
        empty: "Er zijn nog geen managers aangesloten.",
        games: "{count} wedstrijden",
      },
      roster: {
        selectTeam: "Selecteer een team om de spelers te zien.",
        points: "{points} punten",
        empty: "Nog geen spelers gedraft.",
        bench: "Bank",
      },
      transactions: {
        title: "Selectiewijzigingen",
        subtitle: "Automatisch logboek van pickups en drops voor deze competitie.",
        teamFilter: "Teamfilter",
        allTeams: "Alle teams",
        empty: "Geen selectiewijzigingen voor dit filter.",
        picked: "Pakte op",
        dropped: "Dropte",
        meta: "Pick #{pick} / Ronde {round} / Slot {slot}",
      },
    },
    players: {
      title: "Spelers",
      subtitle:
        "Stel het primaire type van elke speler in voor roster-slots. Fantasypunten volgen de werkelijke slag- en werpstatistieken.",
      searchPlaceholder: "Zoek spelers…",
      teamLabel: "Ploeg",
      kindLabel: "Soort",
      teamAll: "Alle teams",
      kindAll: "Alle types",
      kinds: {
        positionPlayer: "Veldspeler",
        pitcher: "Werper",
      },
      columns: {
        name: "Speler",
        team: "Ploeg",
        kind: "Soort",
        games: "GP",
        points: "Punten",
      },
      emptyTitle: "Geen spelers gevonden",
      emptyDescription: "Wis de filters of de zoekterm en probeer opnieuw.",
      loading: "Laden...",
      missingPlayer: "Ontbrekende speler",
      notFoundTitle: "Speler niet gevonden",
      notFoundDescription: "Deze speler is mogelijk verwijderd of de link is verouderd.",
    },
    audit: {
      backToPlayers: "Spelers",
      active: "Actief",
      inactive: "Inactief",
      unknownTeam: "Onbekende ploeg",
      teamAudit: "{team} · opgeslagen score-audit",
      hittingTitle: "Slagstatistieken per wedstrijd",
      hittingSubtitle: "Ruwe slagstats, opgeslagen punten en de huidige scoreberekening.",
      pitchingTitle: "Werpstatistieken per wedstrijd",
      pitchingSubtitle: "Ruwe werpstats, opgeslagen punten en de huidige scoreberekening.",
      emptyTitle: "Geen wedstrijden vastgelegd",
      emptyDescription: "Deze speler heeft nog geen slag- of werpstatregels.",
      totalPoints: "Totaalpunten",
      hitting: "Slaan",
      pitching: "Werpen",
      hGames: "Slagwed.",
      pGames: "Werpwed.",
      rows: "Rijen",
      game: "Wedstrijd",
      points: "Punten",
      why: "Uitleg",
      flags: "Markeringen",
      missingGame: "Wedstrijd ontbreekt",
      ineligible: "Niet speelgerechtigd",
      notFinal: "Niet definitief",
      enteredBy: "Ingevoerd op {date} door {name}",
      nowPoints: "Nu {points}",
      verified: "Gecontroleerd",
      noScoringEvents: "Geen score-events",
      gameReferenceMissing: "Wedstrijdverwijzing ontbreekt",
      gameLink: "wedstrijd",
    },
    games: {
      title: "Wedstrijden",
      subtitle:
        "Plan wedstrijden, voer box-score statistieken in en zie de stand in elke competitie live updaten.",
      schedule: "Wedstrijd plannen",
      list: {
        empty: "Nog geen wedstrijden gepland.",
        emptyTitle: "Leeg schema",
      },
      columnDate: "Datum",
      columnHome: "Thuis",
      columnAway: "Uit",
      columnStatus: "Stand",
      status: {
        scheduled: "Gepland",
        inProgress: "Loopt",
        final: "Eind",
        cancelled: "Afgelast",
        missing: "Ontbreekt",
        filled: "Ingevuld",
      },
      filters: {
        searchPlaceholder: "Zoek teams…",
        statusLabel: "Stand",
        dateLabel: "Datum",
        teamLabel: "Ploeg",
        statusAll: "Alle statussen",
        teamAll: "Alle teams",
      },
      modal: {
        title: "Een wedstrijd plannen",
        date: "Datum",
        home: "Thuisteam",
        away: "Uitteam",
        submit: "Plannen",
        cancel: "Annuleren",
        sameTeam: "Thuis- en uitteam moeten verschillen.",
        submitFail: "Kon wedstrijd niet plannen.",
      },
      entry: {
        eyebrow: "Boxscore",
        markFinal: "Markeer als eindstand",
        unmarkFinal: "Eindstand ongedaan maken",
        save: "Opslaan",
        saveAndFinal: "Opslaan & afronden",
        totalPoints: "Totale fantasy-punten",
        submitFail: "Kon statistieken niet opslaan.",
        dnp: "NG",
        xbhInvalid: "XBH groter dan H",
        unsavedRows: "{count} niet opgeslagen",
        playerHeader: "Speler",
        decisionLabel: "Beslissing",
        noDecision: "Geen",
      },
      import: {
        openButton: "Importeren van KNBSB",
        title: "Box score importeren van KNBSB",
        urlPlaceholder: "Plak een KNBSB-boxscore-URL",
        urlLabel: "KNBSB-boxscore-URL",
        fetch: "Ophalen",
        fetchFail: "Kon de box score niet ophalen.",
        matched: "Gekoppeld ({count})",
        matchedEmpty: "Nog geen gekoppelde spelers.",
        unmatched: "Niet gekoppeld ({count})",
        unmatchedEmpty: "Alles gekoppeld.",
        playerHeader: "Speler",
        pitcherHeader: "Werper",
        confidence: "{pct}% overeenkomst",
        unlink: "Ontkoppelen",
        linkPlaceholder: "Kies een speler…",
        noTeam: "Geen ploeg",
        addPlayer: "Speler toevoegen",
        addPlayerFail: "Kon speler niet toevoegen.",
        applyFail: "Kon stats niet toepassen.",
        otherTeams: "Andere teams",
        apply: "Toepassen ({count})",
        applyBoth: "Toepassen ({hitters} slagmensen, {pitchers} werpers)",
        pitchersMatched: "Werpers gekoppeld ({count})",
        pitchersMatchedEmpty: "Nog geen werpersregels gekoppeld.",
        pitchersUnmatched: "Werpers niet gekoppeld ({count})",
        pitchersUnmatchedEmpty: "Alle werpersregels zijn opgelost.",
        pitchersSaved: "{count} werpersregel(s) opgeslagen.",
        cancel: "Annuleren",
      },
      bulkImport: {
        openButton: "KNBSB bulkimport",
        title: "Bulk importeren van KNBSB",
        urlPlaceholder: "Schema- of box-score-URL",
        dateLabel: "Wedstrijddatum",
        addUrl: "Toevoegen",
        preview: "Voorbeeld",
        removeUrl: "{url} verwijderen",
        fetchFail: "Kon KNBSB-wedstrijden niet ophalen.",
        skipped: "{count} niet-afgeronde wedstrijden overgeslagen.",
        matchCounts: "{matched} gekoppeld / {unmatched} niet gekoppeld",
        pitchCounts: "P: {matched} gekoppeld / {unmatched} niet gekoppeld",
        noStatus: "Geen status",
        newGame: "Nieuw",
        hasStats: "Heeft stats",
        imported: "Geïmporteerd",
        confirm: "Deze wedstrijd importeren",
        confirmAll: "Alles importeren ({count})",
        replaceAll: "Alle stats vervangen ({count})",
        importReplaceAll: "Alles importeren/vervangen ({count})",
        importProgress: "Importeren {current}/{total} · {game}",
        importFail: "Kon deze wedstrijd niet importeren.",
        replace: "Stats vervangen",
      },
      todayImport: {
        button: "IMPORT vandaag",
        success: "{games} wedstrijd(en) van vandaag geimporteerd. {players} speler(s) toegevoegd.",
        partial:
          "{imported} wedstrijd(en) geimporteerd, {failed} mislukt. {players} speler(s) toegevoegd.",
        empty:
          "Geen definitieve KNBSB-wedstrijden om vandaag te importeren. {skipped} onafgeronde wedstrijd(en) overgeslagen.",
        fail: "Kon de KNBSB-wedstrijden van vandaag niet importeren.",
      },
      errors: {
        gameHasStats: "Kan een wedstrijd met ingevoerde statistieken niet verwijderen.",
        invalidStatLine:
          "Statistiekregel is ongeldig (extra-base hits zijn meer dan het totaal aantal hits).",
        gameNotEligible: "Deze wedstrijd komt niet in aanmerking voor statistieken.",
      },
      headers: {
        AB: "AB",
        H: "H",
        D2B: "2B",
        D3B: "3B",
        HR: "HR",
        RBI: "RBI",
        R: "R",
        BB: "BB",
        SB: "SB",
        CS: "CS",
        SO: "SO",
        PTS: "PTS",
        IP: "IP",
        ER: "ER",
        DEC: "DEC",
      },
    },
    analytics: {
      title: "Analytics",
      subtitle: "Seizoens- en weekklassementen voor alle gedrafte spelers.",
      tabs: {
        all: "Alles",
        hitters: "Slagmensen",
        pitchers: "Werpers",
      },
      sections: {
        seasonLeaders: "Seizoensleiders",
        disciplineCold: "Discipline & koud",
        formInsights: "Vorm & inzichten",
        thisWeek: "Deze week",
        teams: "Ploegen",
      },
      leaderboards: {
        topSeason: "Topprestaties (seizoen)",
        bottomSeason: "Slechtste prestaties (seizoen)",
        topWeek: "Topprestaties deze week",
        topWeekAll: "Topprestaties deze week — Alles",
        topWeekHitters: "Topslagmensen deze week",
        topWeekPitchers: "Topwerpers deze week",
        pitcherTopSeason: "Topwerpers (seizoen)",
        pitcherWorstSeason: "Slechtste werpers (seizoen)",
        wins: "Wins-leiders",
        pitcherK: "Strike-out-leiders",
        ip: "Innings-leiders",
        era: "ERA-leiders",
        whip: "WHIP-leiders",
        k9: "K/9-leiders",
        bb9: "BB/9 (wildheid)",
        pitcherRecentForm: "Recente vorm (werpers)",
        pitcherHotStreaks: "Hete reeksen (werpers)",
        pitcherColdStreaks: "Koude reeksen (werpers)",
        pitcherConsistency: "Meest constante werpers",
        pitcherBestGames: "Beste werpprestaties",
        pitcherRisers: "Stijgers (werpers)",
        pitcherFallers: "Dalers (werpers)",
        hr: "HR-leiders",
        rbi: "RBI-leiders",
        sb: "SB-leiders",
        avg: "AVG-leiders",
        hits: "Hits-leiders",
        runs: "Runs-leiders",
        bb: "BB-leiders",
        tb: "Total bases-leiders",
        xbh: "XBH-leiders",
        so: "Meeste strike-outs",
        obp: "OBP-leiders",
        slg: "SLG-leiders",
        ops: "OPS-leiders",
        kRate: "Hoogste K-rate",
        bbRate: "Hoogste BB-rate",
        recentForm: "Recente vorm",
        hotStreaks: "Hete reeksen",
        coldStreaks: "Koude reeksen",
        consistency: "Meest constant",
        bestGames: "Beste wedstrijdprestaties",
        risers: "Stijgers deze week",
        fallers: "Dalers deze week",
        teamScoring: "Teamscores totaal",
        teamTopPerformers: "Topscorers per team",
      },
      teamFilter: {
        all: "Alle teams",
        label: "Ploeg",
      },
      columns: {
        rank: "#",
        player: "Speler",
        team: "Ploeg",
        games: "GP",
        points: "PTS",
        value: "Waarde",
        opp: "Tegen",
        date: "Datum",
        line: "Lijn",
        through: "T/m",
        streak: "Reeks",
        mean: "Gem.",
        stdDev: "σ",
        priorWeek: "Vorige",
        currentWeek: "Huidig",
        delta: "Δ",
        players: "Spelers",
        fpPerGame: "FP/G",
        totalFp: "Totale FP",
        ip: "IP",
        era: "ERA",
        whip: "WHIP",
        k9: "K/9",
        bb9: "BB/9",
        wins: "W",
        k: "K",
        er: "ER",
      },
      eyebrow: {
        minGames: "Min. {count} wedstrijden",
        minAb: "Min. {count} AB",
        minIp: "Min. {count} IP",
        week: "Week {week}",
        lastN: "Laatste {count} wedstrijden",
        weekDelta: "Δ vs vorige week",
      },
      empty: "Nog niet genoeg slagbeurten.",
    },
    scoring: {
      title: "Puntensysteem",
      subtitle:
        "Hoe fantasypunten worden toegekend voor elk slaggebeurtenis. Alleen-lezen — om te delen met deelnemers.",
      version: "Regelset v{version}",
      positive: "Positieve scoring",
      penalties: "Strafpunten",
      columns: {
        rule: "Gebeurtenis",
        abbr: "Afk.",
        points: "Punten",
      },
      rules: {
        single: "Honkslag",
        double: "Tweehonkslag",
        triple: "Driehonkslag",
        homeRun: "Homerun",
        rbi: "Binnengeslagen punt",
        run: "Gescoorde run",
        walk: "Vier wijd",
        stolenBase: "Gestolen honk",
        caughtStealing: "Gepakt bij stelen",
        strikeout: "Strike-out",
      },
      pitcherTitle: "Werpersscoresysteem",
      pitcherSubtitle:
        "Hoe fantasypunten worden toegekend voor elk werpersmoment. Elke speler in een roster met werpstatistieken krijgt deze punten.",
      pitcherVersion: "Werpersregels v{version}",
      pitcherNote:
        "Innings worden opgeslagen als outs (3 outs = 1 IP); weergave volgt de KNBSB-conventie .0/.1/.2. Beslissingen (W/L) komen uit de box score.",
      pitcherRules: {
        perOut: "Out gehaald (1 IP = 3 outs)",
        perStrikeout: "Strike-out",
        perWin: "Winst",
        perSave: "Save noteren",
        perCompleteGame: "Volledige wedstrijd",
        perShutout: "Nulwedstrijd",
        perEarnedRun: "Verdiend punt tegen",
        perWalk: "Vier wijd toegestaan",
        perHitAllowed: "Hit toegestaan",
        perLoss: "Verlies",
      },
      note: "De totale punten van een speler zijn de som van elke gebeurtenis over alle wedstrijden. De regelset wordt per league bij aanmaak vastgelegd en verandert niet tijdens het seizoen.",
      maintenance: "Onderhoud",
      saving: "Opslaan…",
      saved: "Opgeslagen. Voer «Alle stats bijwerken» uit om dit op bestaande stats toe te passen.",
      savedHint:
        "Voer «Alle stats bijwerken» uit om dit op bestaande stats toe te passen.",
      rescoreButton: "Alle stats bijwerken",
      rescoreRunning: "Bezig met herberekenen…",
      rescoreConfirm:
        "Huidige slag- en werpregels toepassen op elke bestaande statsrij, seizoenstotalen herberekenen en afgeleide historische profielscore verversen?",
      rescoreDone: "{count} slag- en werprijen herberekend.",
      rescoreHint:
        "Voer dit uit nadat je puntenwaarden hebt aangepast, zodat wedstrijden, standen, weektotalen, werpersbonussen en historische profielen dezelfde regels gebruiken.",
      dangerZone: "Gevarenzone",
      purgeTitle: "Alle stats wissen",
      purgeSubtitle:
        "Verwijdert elke ingevoerde box-scoreregel en zet de seizoenstotalen van elke manager terug op nul. Drafts, competities, spelers en wedstrijden blijven behouden. Handig om het seizoen schoon te beginnen.",
      purgeButton: "Alle stats wissen",
      purgeRunning: "Bezig met wissen…",
      purgeConfirm1:
        "Dit verwijdert ALLE ingevoerde box-scoreregels en zet de seizoenstotalen van elke manager op nul. Doorgaan?",
      purgeConfirm2: "Laatste kans — kan niet ongedaan worden gemaakt. Echt alles wissen?",
      purgeDone: "{stats} rijen verwijderd · {totals} managertotalen gereset.",
    },
    cutPicks: {
      title: "Laatste X picks per manager schrappen",
      subtitle:
        "Verwijdert per manager de meest recente picks (hoogste picknummers) in een competitie. Handig als de draft te veel rondes had en je de selecties wilt verkleinen vóór de seizoenstart.",
      leagueLabel: "Competitie",
      leaguePlaceholder: "Kies een competitie…",
      rosterTag: "{members} managers · selectie {size}",
      countLabel: "Aantal",
      adjustLabel: "Selectiegrootte mee verkleinen",
      button: "Schrap laatste {count}",
      running: "Bezig met schrappen…",
      confirm1: "Laatste {count} pick(s) van elke manager in “{league}” verwijderen?",
      confirm2: "Laatste kans — kan niet ongedaan worden gemaakt. Doorgaan?",
      done: "{picks} picks verwijderd bij {members} managers · selectie nu {roster}.",
    },
  },
};

export const dictionaries = { en, es, nl } satisfies Record<Locale, Dictionary>;

export type Locale = "en" | "es" | "nl";
export const SUPPORTED_LOCALES: readonly Locale[] = ["en", "es", "nl"] as const;

export const LOCALE_LABEL: Record<Locale, string> = {
  en: "EN",
  es: "ES",
  nl: "NL",
};
